import * as THREE from 'three';
import { splitSpawns } from './CrossfireLevel.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { TextureFactory, SURFACES } from '../render/Materials.js';

/* ------------------------------------------------------------ helpers ---- */

const _m4 = new THREE.Matrix4();
const _m4b = new THREE.Matrix4();
const _eu = new THREE.Euler();
const _q = new THREE.Quaternion();
const _v3 = new THREE.Vector3();
const _s3 = new THREE.Vector3(1, 1, 1);
const _yAxis = new THREE.Vector3(0, 1, 0);

/**
 * Box with world-uniform UVs: every face is unwrapped at `tile` metres per
 * texture repeat, so a 12 m wall and a 0.2 m cornice share texel density even
 * after everything has been merged into one buffer.
 */
function boxGeo(w, h, d, tile = 2) {
  const g = new THREE.BoxGeometry(w, h, d);
  const uv = g.attributes.uv;
  const dims = [[d, h], [d, h], [w, d], [w, d], [w, h], [w, h]];
  for (let f = 0; f < 6; f++) {
    for (let i = 0; i < 4; i++) {
      const k = f * 4 + i;
      uv.setXY(k, (uv.getX(k) * dims[f][0]) / tile, (uv.getY(k) * dims[f][1]) / tile);
    }
  }
  return g;
}

function xf(g, x = 0, y = 0, z = 0, ry = 0, rx = 0, rz = 0) {
  if (rx || ry || rz) {
    _eu.set(rx, ry, rz, 'YXZ');
    g.applyMatrix4(_m4.makeRotationFromEuler(_eu));
  }
  g.translate(x, y, z);
  return g;
}

/**
 * Shift (and optionally mirror) a geometry's UVs. Used to decorrelate the tile
 * phase per building: plaster repeats every few metres, so without this a run
 * of facades lines its spall and streaking up like wallpaper.
 */
function uvShift(g, du, dv, flip = 1) {
  const uv = g.attributes.uv;
  if (!uv) return g;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * flip + du, uv.getY(i) + dv);
  return g;
}

/**
 * Materials whose geometry is unwrapped 0..1 across a single quad (canvas
 * decals). Phase-shifting these would slide the artwork off its quad, so the
 * per-building offset must skip them.
 */
const UV01_KEYS = new Set(['poster0', 'poster1', 'poster2', 'graffiti0', 'graffiti1']);

/**
 * Ground ribbon lying in XZ with its normal up: U spans 0..1 across the width
 * (so a soft-shouldered decal texture fits it exactly whatever the width) and V
 * repeats every `vTile` metres down the length. The road-decal workhorse.
 */
function ribbonGeo(w, len, vTile = 3, uTile = 0) {
  const g = new THREE.PlaneGeometry(w, len, 3, Math.max(1, Math.round(len / 2)));
  g.rotateX(-Math.PI / 2);
  const uv = g.attributes.uv;
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, uTile > 0 ? (uv.getX(i) * w) / uTile : uv.getX(i), (uv.getY(i) * len) / vTile);
  }
  return g;
}

function planeGeo(w, h, tile = 0) {
  const g = new THREE.PlaneGeometry(w, h);
  if (tile > 0) {
    const uv = g.attributes.uv;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, (uv.getX(i) * w) / tile, (uv.getY(i) * h) / tile);
  }
  return g;
}

/** Deterministic LCG — the map must look identical in every capture. */
function makeRng(seed) {
  let s = seed >>> 0;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}

/** Collects geometry per material key and merges it into one mesh each. */
class Batcher {
  constructor() { this.map = new Map(); }

  add(key, geo) {
    for (const name of Object.keys(geo.attributes)) {
      if (name !== 'position' && name !== 'normal' && name !== 'uv') geo.deleteAttribute(name);
    }
    if (!geo.attributes.uv) {
      const n = geo.attributes.position.count;
      geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2));
    }
    if (!geo.attributes.normal) geo.computeVertexNormals();
    if (!geo.index) {
      const n = geo.attributes.position.count;
      const idx = n > 65535 ? new Uint32Array(n) : new Uint16Array(n);
      for (let i = 0; i < n; i++) idx[i] = i;
      geo.setIndex(new THREE.BufferAttribute(idx, 1));
    }
    let arr = this.map.get(key);
    if (!arr) { arr = []; this.map.set(key, arr); }
    arr.push(geo);
    return geo;
  }

  flush(materials, target) {
    let tris = 0;
    for (const [key, list] of this.map) {
      const merged = mergeGeometries(list, false);
      if (!merged) { console.warn('[Level] merge failed for', key); continue; }
      merged.computeBoundingSphere();
      const mesh = new THREE.Mesh(merged, materials[key]);
      mesh.castShadow = mesh.receiveShadow = true;
      mesh.name = 'batch_' + key;
      target.add(mesh);
      tris += merged.index.count / 3;
    }
    this.map.clear();
    return tris;
  }
}

/* ------------------------------------------------ local surface presets --- */
/* Defined here rather than in Materials.js so the two files stay decoupled.  */

const WOOD = {
  seed: 3311, octaves: 4, period: 4, normalStrength: 1.6,
  albedo: (n, x, y, d) => {
    const plank = Math.floor(y * 7);
    const shade = 0.78 + ((plank * 37) % 11) / 34;
    const grain = 0.5 + 0.5 * Math.sin(x * 41 + n * 11 + plank * 5);
    const b = (0.26 + n * 0.15 + grain * 0.07) * shade;
    return [b * 1.2, b * 0.9, b * 0.63];
  },
  height: (n, x, y) => {
    const py = (y * 7) % 1;
    return n * 0.3 + (py < 0.07 || py > 0.93 ? 0.05 : 0.7);
  },
  rough: (n, d) => 0.84 + d * 0.12,
  ao: (n, d, h) => 0.66 + h * 0.34,
  metal: () => 0,
};

const FABRIC = {
  seed: 9091, octaves: 4, period: 10, normalStrength: 1.0,
  albedo: (n, x, y, d) => {
    const weave = 0.5 + 0.25 * (Math.sin(x * 260) + Math.sin(y * 260));
    const b = 0.46 + n * 0.2 + weave * 0.08;
    return [b, b * 0.96, b * 0.9];
  },
  height: (n, x, y) => 0.5 + 0.25 * (Math.sin(x * 260) + Math.sin(y * 260)),
  rough: () => 0.92,
  ao: (n, d, h) => 0.8 + h * 0.2,
  metal: () => 0,
};

const TILEFLOOR = {
  seed: 616, octaves: 5, period: 6, normalStrength: 1.9,
  albedo: (n, x, y, d) => {
    const gx = (x * 6) % 1, gy = (y * 6) % 1;
    const joint = gx < 0.045 || gy < 0.045 ? 0.55 : 1;
    const cell = ((Math.floor(x * 6) * 7 + Math.floor(y * 6) * 13) % 5) / 24;
    const b = (0.40 + n * 0.14 + cell) * joint;
    return [b * 1.02, b * 0.99, b * 0.93];
  },
  height: (n, x, y) => {
    const gx = (x * 6) % 1, gy = (y * 6) % 1;
    return gx < 0.045 || gy < 0.045 ? 0.1 : 0.65 + n * 0.35;
  },
  rough: (n, d) => 0.7 + d * 0.2,
  ao: (n, d, h) => 0.6 + h * 0.4,
  metal: () => 0,
};

/* Face frames: 0:+Z 1:-Z 2:+X 3:-X. Local space is x-along-face, y-up,
   +z pointing out of the wall with the outer facade plane at z = 0.        */
const FACE_RY = [0, Math.PI, Math.PI / 2, -Math.PI / 2];

const WALL_T = 0.85;   // cladding thickness -> window reveal depth

/**
 * "Al-Rasheed District" — a compact three-lane urban block.
 *
 * Buildings are a dark structural core wrapped in a cladding shell built from
 * piers and spandrels, so window openings are real holes with 0.85 m reveals
 * rather than black stickers. All of that trim, plus every piece of street
 * dressing, is merged per material by `Batcher`, which keeps the whole map in
 * the region of 40 draw calls. Physics only ever sees `collision`.
 */
export class Level {
  /**
   * Bomb sites for the procedural urban block. Authored with tools/_sitescout:
   * two open, walkable areas 75 m apart, each ~17 m from the nearest spawn so
   * neither side starts on top of one. A = the western lot, B = the northern
   * arcade end.
   */
  static bombSites = [
    { label: 'A', box: [-13.5, 0.8, -26.5, -4.5, 4.0, -17.5] },
    { label: 'B', box: [7.5, 0.0, 45.5, 16.5, 3.2, 54.5] },
  ];

  constructor(engine) {
    this.engine = engine;
    this.root = new THREE.Group();
    this.collision = new THREE.Group();
    this.decor = new THREE.Group();
    this.root.add(this.collision, this.decor);

    this.tex = new TextureFactory(engine.renderer, 512);
    this.spawnPoints = [];
    this.coverPoints = [];

    this.rng = makeRng(20250726);
    this.B = new Batcher();          // decorative merged geometry
    this.C = new Batcher();          // merged geometry that also blocks

    this._buildMaterials();
    this._buildGround();
    this._buildBlocks();
    this._buildStreetFurniture();
    this._buildCompositions();
    this._buildScatter();
    this._buildSpawns();

    const decorTris = this.B.flush(this.mat, this.decor);
    const solidTris = this.C.flush(this.mat, this.collision);
    this.stats = { decorTris, solidTris };

    this.root.traverse((o) => {
      if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; }
    });
    // Ground, road and every flat road decal read better without self-shadow
    // acne — and a decal that casts is a decal that draws its own outline.
    for (const n of ['terrain', 'roadNS', 'roadEW',
      'batch_dust', 'batch_polish', 'batch_paint', 'batch_patch',
      'batch_scorch', 'batch_puddle', 'batch_grime']) {
      this.root.traverse((o) => { if (o.name === n) o.castShadow = false; });
    }
  }

  /* --------------------------------------------------------- materials --- */

  _buildMaterials() {
    const T = this.tex;
    const std = (name, spec, o = {}) => T.material(name, spec, { repeat: 1, ...o });

    const plaster = std('plaster', SURFACES.plaster, { normalScale: 0.9 });
    const concrete = std('concrete', SURFACES.concrete, { normalScale: 1.1 });

    const tint = (base, hex, rough) => {
      const m = base.clone();
      m.color = new THREE.Color(hex);
      if (rough !== undefined) m.roughness = rough;
      return m;
    };

    // The sun in this map is low and very warm, so cool the neutral surfaces
    // back down with the material tint or everything reads orange.
    this.mat = {
      // Albedo is authored linear and sRGB-encoded on pack, so the road needs
      // no correction tint. It does get a light cool cast: this sun is warm
      // enough that neutral asphalt renders as terracotta, and bitumen is the
      // one surface in the map that must stay grey to read as a road at all.
      road: std('road', SURFACES.asphalt, { normalScale: 1.2, color: 0xdfe6ec }),
      dirt: std('dirt', SURFACES.sand, { normalScale: 1.0 }),
      concrete,
      concreteDark: tint(concrete, 0x8c8880),
      kerb: tint(concrete, 0xa8a6a0),
      plasterWarm: tint(plaster, 0xb59a7c),
      plasterSand: tint(plaster, 0xa8977a),
      plasterCool: tint(plaster, 0x9a968e),
      plasterPale: tint(plaster, 0xc2bcae),
      plasterTeal: tint(plaster, 0x8d9a94),
      brick: std('brick', SURFACES.brick, { normalScale: 1.2 }),
      metal: std('rustMetal', SURFACES.rustMetal, { normalScale: 1.3 }),
      gunmetal: std('gunmetal', SURFACES.gunmetal, { normalScale: 0.9 }),
      wood: std('lvlWood', WOOD, { normalScale: 1.4 }),
      fabric: std('lvlFabric', FABRIC, { normalScale: 0.9 }),
      tile: std('lvlTile', TILEFLOOR, { normalScale: 1.4, color: 0x7d7a72 }),
      sandbag: std('sandbag', SURFACES.sand, { normalScale: 1.5 }),
    };
    this.mat.sandbag.color = new THREE.Color(0x8d7f5f);
    this.mat.wood.color = new THREE.Color(0x71583c);
    this.mat.fabricRed = tint(this.mat.fabric, 0x8c5244);
    this.mat.fabricBlue = tint(this.mat.fabric, 0x6f8ca2);
    this.mat.fabricCream = tint(this.mat.fabric, 0xd0c3a6);
    this.mat.metalRed = tint(this.mat.metal, 0xa4483a);
    this.mat.metalBlue = tint(this.mat.metal, 0x486a7c);
    this.mat.metalGreen = tint(this.mat.metal, 0x4f6248);
    this.mat.metalYellow = tint(this.mat.metal, 0xa08a3e);

    // Interior void behind the window openings — unlit-dark, slightly warm so
    // it does not read as a pure black hole in shadow.
    this.mat.interior = new THREE.MeshStandardMaterial({
      color: 0x0a0a0b, roughness: 1.0, metalness: 0.0,
    });
    // Burnt-out vehicles. An untextured dark material turns the bus into a
    // black cut-out at any distance — it has no grain to catch the sun and no
    // normal to break the silhouette up. Driving it off the rust maps keeps the
    // scorched value while giving it panel grain, edge highlights and specular.
    this.mat.burnt = std('rustMetal', SURFACES.rustMetal, {
      normalScale: 1.5, color: 0x6a5b49, roughness: 1.0, envMapIntensity: 0.9,
    });
    this.mat.tyre = new THREE.MeshStandardMaterial({
      color: 0x2e2c2a, roughness: 0.92, metalness: 0.0,
      normalMap: this.mat.road.normalMap,
      normalScale: new THREE.Vector2(1.1, 1.1),
    });
    // Dim, warm shop interior seen through the ground-floor glazing.
    this.mat.interiorWarm = new THREE.MeshStandardMaterial({
      color: 0x4a3f30, roughness: 1.0, metalness: 0.0,
      emissive: 0x2a2118, emissiveIntensity: 1.0,
    });
    // Road decals reuse the asphalt maps. An untextured flat colour lit by this
    // sun turns into a brown card lying on the street; keeping the grain and
    // the normal map makes a patch read as a patch.
    const decal = (colour, extra = {}) => {
      const m = std('road', SURFACES.asphalt, { normalScale: 0.8, color: colour });
      m.polygonOffset = true;
      m.polygonOffsetFactor = -3;
      m.polygonOffsetUnits = -3;
      Object.assign(m, extra);
      return m;
    };
    // Road markings. Thermoplastic is the brightest thing on a carriageway, so
    // it needs a bright base map, not a mid-grey flat colour — and a cool tint,
    // because this sun is warm enough to turn anything neutral into terracotta.
    this.mat.paint = std('concrete', SURFACES.concrete, {
      normalScale: 0.45, color: 0xb9bfc4, roughness: 1.0,
    });
    this.mat.paint.polygonOffset = true;
    this.mat.paint.polygonOffsetFactor = -4;
    this.mat.paint.polygonOffsetUnits = -4;
    this.mat.patch = decal(0xa4a4a4);
    // Wheel-track burnishing. Rubber-polished asphalt differs from the rest of
    // the carriageway mostly in *sheen*, not value, so this is a roughness
    // decal: near-neutral albedo, much smoother, so the tracks light up as two
    // ribbons under a low sun and go quiet when the sun is behind you.
    this.mat.polish = decal(0xc4c4c4, { roughness: 0.44, envMapIntensity: 1.7 });
    this.mat.scorch = decal(0x4c4c4c, { transparent: true, opacity: 0.9 });
    // Wet asphalt rather than a mirror: it keeps the road's normal map so the
    // reflection breaks up instead of reading as a sheet of brown plastic.
    this.mat.puddle = new THREE.MeshPhysicalMaterial({
      color: 0x0b0d10, roughness: 0.14, metalness: 0.0,
      normalMap: this.mat.road.normalMap,
      normalScale: new THREE.Vector2(0.22, 0.22),
      envMapIntensity: 1.3, reflectivity: 0.7, clearcoat: 1.0, clearcoatRoughness: 0.09,
      polygonOffset: true, polygonOffsetFactor: -5, polygonOffsetUnits: -5,
    });
    this.mat.glass = new THREE.MeshPhysicalMaterial({
      color: 0x0a1016, roughness: 0.06, metalness: 0.0,
      envMapIntensity: 2.8, reflectivity: 0.9, clearcoat: 1.0, clearcoatRoughness: 0.04,
      side: THREE.DoubleSide,
    });
    this.mat.glassBroken = this.mat.glass.clone();
    this.mat.glassBroken.side = THREE.DoubleSide;
    // Shopfront glazing sits in front of a dressed interior, so it must be a
    // window and not a mirror: drop the reflection until the stock behind it
    // wins. Upper-floor glass keeps the full mirror — there is nothing behind.
    this.mat.glassShop = this.mat.glass.clone();
    this.mat.glassShop.envMapIntensity = 0.85;
    this.mat.glassShop.reflectivity = 0.45;
    this.mat.glassShop.color = new THREE.Color(0x14181c);
    this.mat.lamp = new THREE.MeshStandardMaterial({
      color: 0x2a2a28, emissive: 0xffb066, emissiveIntensity: 2.6, roughness: 0.5,
    });

    this._buildDecalMaterials();
  }

  /** Posters and graffiti, drawn to canvases here so Materials.js is untouched. */
  _buildDecalMaterials() {
    const rng = makeRng(778811);
    const posterCanvas = (i) => {
      const c = document.createElement('canvas');
      c.width = 256; c.height = 384;
      const g = c.getContext('2d');
      const bg = ['#8c3a2e', '#2f4a63', '#7a6a30', '#5a4030'][i % 4];
      g.fillStyle = bg; g.fillRect(0, 0, 256, 384);
      g.globalAlpha = 0.25;
      for (let k = 0; k < 60; k++) {
        g.fillStyle = rng() > 0.5 ? '#000' : '#fff';
        g.fillRect(rng() * 256, rng() * 384, rng() * 40 + 4, rng() * 8 + 2);
      }
      g.globalAlpha = 1;
      // A portrait-ish block and stacked "text" bars: reads as propaganda.
      g.fillStyle = 'rgba(0,0,0,0.35)';
      g.fillRect(38, 40, 180, 200);
      g.fillStyle = 'rgba(230,225,210,0.85)';
      for (let r = 0; r < 5; r++) {
        const w = 60 + rng() * 150;
        g.fillRect(28, 268 + r * 22, w, 9);
      }
      // Tear the edges so the poster is not a clean rectangle.
      g.globalCompositeOperation = 'destination-out';
      for (let k = 0; k < 26; k++) {
        g.beginPath();
        const edge = k % 4;
        const x = edge === 2 ? rng() * 256 : edge === 0 ? 0 : edge === 1 ? 256 : rng() * 256;
        const y = edge === 3 ? 384 : edge < 2 ? rng() * 384 : 0;
        g.arc(x, y, 8 + rng() * 22, 0, Math.PI * 2);
        g.fill();
      }
      g.globalCompositeOperation = 'source-over';
      return c;
    };

    const graffitiCanvas = () => {
      const c = document.createElement('canvas');
      c.width = 512; c.height = 256;
      const g = c.getContext('2d');
      g.clearRect(0, 0, 512, 256);
      const cols = ['#c8442e', '#e0d24a', '#3f7fc0', '#e8e4d8', '#2f9a5e'];
      for (let pass = 0; pass < 3; pass++) {
        g.strokeStyle = cols[(rng() * cols.length) | 0];
        g.lineWidth = 10 + rng() * 18;
        g.lineCap = 'round';
        g.globalAlpha = 0.55 + rng() * 0.4;
        g.beginPath();
        let x = 30 + rng() * 80, y = 90 + rng() * 80;
        g.moveTo(x, y);
        for (let k = 0; k < 5; k++) {
          const nx = x + 40 + rng() * 90;
          const ny = 40 + rng() * 170;
          g.quadraticCurveTo(x + 30, y - 60 + rng() * 120, nx, ny);
          x = nx; y = ny;
        }
        g.stroke();
      }
      return c;
    };

    const decalMat = (canvas, opts = {}) => {
      const t = new THREE.CanvasTexture(canvas);
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = this.tex.maxAniso;
      return new THREE.MeshStandardMaterial({
        map: t, transparent: true, alphaTest: 0.12, roughness: 0.98, metalness: 0, color: 0x8e8e8e,
        side: THREE.DoubleSide, depthWrite: false,
        polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4,
        ...opts,
      });
    };

    /**
     * Wind-blown dust film. This is the other half of the wheel-track read: the
     * tracks themselves are just swept asphalt, and what actually makes them
     * legible is the pale grit that builds up everywhere the tyres don't go.
     * Soft-shouldered in U so the band edges dissolve instead of cutting.
     */
    const dustCanvas = () => {
      const c = document.createElement('canvas');
      c.width = 64; c.height = 256;
      const g = c.getContext('2d');
      const img = g.createImageData(64, 256);
      for (let y = 0; y < 256; y++) {
        for (let x = 0; x < 64; x++) {
          const u = x / 63, v = y / 255;
          let a = Math.sin(Math.PI * u) ** 1.5;                 // soft shoulders
          a *= 0.62 + 0.38 * Math.sin(u * 17 + Math.sin(v * 8.3) * 2.2);   // streaks
          a *= 0.5 + 0.5 * Math.sin(v * 12.7 + u * 2.6) * Math.sin(v * 3.7 + 1.1);
          a = Math.max(0, Math.min(1, a * 1.35));
          const n = 0.8 + rng() * 0.2;
          const o = (y * 64 + x) * 4;
          img.data[o] = 198 * n; img.data[o + 1] = 188 * n; img.data[o + 2] = 168 * n;
          img.data[o + 3] = a * 150;
        }
      }
      g.putImageData(img, 0, 0);
      return c;
    };
    this.mat.dust = decalMat(dustCanvas(), {
      alphaTest: 0, roughness: 1.0, color: 0xffffff,
      polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    });
    this.mat.dust.map.wrapS = THREE.ClampToEdgeWrapping;
    this.mat.dust.map.wrapT = THREE.RepeatWrapping;
    // Same soft-shouldered mask, inverted in value: damp, oil and grime. Sharing
    // the texture means the two read as the same weather acting on the surface.
    this.mat.grime = this.mat.dust.clone();
    this.mat.grime.color = new THREE.Color(0x33302a);

    this.mat.poster0 = decalMat(posterCanvas(0));
    this.mat.poster1 = decalMat(posterCanvas(1));
    this.mat.poster2 = decalMat(posterCanvas(2));
    this.mat.graffiti0 = decalMat(graffitiCanvas());
    this.mat.graffiti1 = decalMat(graffitiCanvas());
  }

  _add(mesh, solid = true) {
    (solid ? this.collision : this.decor).add(mesh);
    return mesh;
  }

  /** Invisible octree proxy for instanced / merged-decor props. */
  _proxy(w, h, d, x, y, z, ry = 0) {
    const p = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshBasicMaterial());
    p.position.set(x, y, z);
    p.rotation.y = ry;
    p.visible = false;
    p.castShadow = p.receiveShadow = false;
    this.collision.add(p);
    return p;
  }

  /* ------------------------------------------------------------ ground --- */

  /** Grid surface with a height function; used for terrain and cambered road. */
  _slab(x0, x1, z0, z1, nx, nz, yFn, tile) {
    const g = new THREE.PlaneGeometry(x1 - x0, z1 - z0, nx, nz);
    g.rotateX(-Math.PI / 2);
    const pos = g.attributes.position;
    const uv = g.attributes.uv;
    const cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i) + cx;
      const z = pos.getZ(i) + cz;
      pos.setXYZ(i, x, yFn(x, z), z);
      uv.setXY(i, x / tile, z / tile);
    }
    g.computeVertexNormals();
    return g;
  }

  /** Distance outside the paved envelope — drives terrain roughness. */
  _pavedDist(x, z) {
    const d = [];
    d.push(Math.max(Math.abs(x) - 16, Math.abs(z) - 92));            // main street
    d.push(Math.max(Math.abs(z + 14) - 12, Math.abs(x) - 62));       // cross street
    d.push(Math.max(Math.abs(x - 32) - 17, Math.abs(z - 28) - 6));   // market lane
    d.push(Math.max(Math.abs(x + 45) - 8, Math.abs(z + 8) - 56));    // west back lane
    return Math.max(0, Math.min(...d));
  }

  _buildGround() {
    const rngT = makeRng(4242);
    const bumps = [];
    for (let i = 0; i < 14; i++) {
      bumps.push([(rngT() - 0.5) * 220, (rngT() - 0.5) * 220, 8 + rngT() * 26, (rngT() - 0.4) * 0.9]);
    }
    const terrainY = (x, z) => {
      const fade = THREE.MathUtils.clamp((this._pavedDist(x, z) - 1.5) / 7, 0, 1);
      let h = 0;
      for (const [bx, bz, r, a] of bumps) {
        const dd = Math.hypot(x - bx, z - bz) / r;
        if (dd < 1) h += a * (1 - dd * dd) * (1 - dd * dd);
      }
      h += Math.sin(x * 0.21) * Math.cos(z * 0.17) * 0.12;
      return -0.02 + h * fade;
    };

    const terrain = new THREE.Mesh(this._slab(-120, 120, -120, 120, 44, 44, terrainY, 10), this.mat.dirt);
    terrain.name = 'terrain';
    terrain.receiveShadow = true;
    this._add(terrain);
    // Skirt box so nothing can be seen or fallen through under the terrain.
    const under = new THREE.Mesh(new THREE.BoxGeometry(244, 3, 244), this.mat.dirt);
    under.position.y = -2.2;
    this._add(under);

    // Cambered asphalt: crown in the middle, water runs to the gutters.
    const camber = (t) => 0.19 - 0.19 * t * t;     // t = normalised distance from crown
    // Runs 0.6 m wider than the kerb line so the asphalt tucks under the kerb
    // instead of leaving a sliver of terrain in the gutter.
    const roadNS = new THREE.Mesh(
      this._slab(-10.2, 10.2, -92, 92, 12, 90, (x) => camber(Math.min(1, Math.abs(x) / 9.6)), 2.0),
      this.mat.road
    );
    roadNS.name = 'roadNS';
    this._add(roadNS);

    for (const [x0, x1] of [[-62, -9.8], [9.8, 62]]) {
      const road = new THREE.Mesh(
        this._slab(x0, x1, -22.4, -5.6, 30, 9, (x, z) => camber(Math.min(1, Math.abs(z + 14) / 8.0)) * 0.9, 2.0),
        this.mat.road
      );
      road.name = 'roadEW';
      this._add(road);
    }

    this._buildSidewalks();
    this._buildRoadDressing();
    this._buildPavementDressing();
  }

  /**
   * The pavements are the largest single surface a player ever looks at on this
   * map and they were reading as clean grey card. Everything here exists to
   * break that up at three ranges: lifted and missing slabs (silhouette),
   * stains and grit (mid), bollards and clutter (near, and something to hug).
   */
  _buildPavementDressing() {
    const rng = makeRng(24680);
    const TOP = 0.245;
    const junction = (z) => z > -23.5 && z < -4.5;

    for (const s of [-1, 1]) {
      const inner = s * 10.2;          // just in from the kerb
      const outer = s * 15.6;          // against the building plinth

      // Lifted, cracked and missing slabs. A gap gets a dark sub-base so it
      // reads as a hole rather than a painted square.
      for (let z = -88; z < 88; z += 4.2) {
        if (rng() > 0.30 || junction(z)) continue;
        const px = s * (10.4 + rng() * 4.6);
        const sw = 0.75 + rng() * 0.35;
        if (rng() < 0.4) {
          this.B.add('concreteDark', xf(boxGeo(sw, 0.16, sw, 1.2),
            px, TOP + 0.02, z, rng() * 3, (rng() - 0.5) * 0.5, (rng() - 0.5) * 0.5));
        } else {
          this.B.add('dirt', xf(boxGeo(sw + 0.1, 0.1, sw + 0.1, 1.0), px, TOP - 0.12, z));
        }
      }

      // Desire line: a century of feet polishes a dark path down the middle of
      // any pavement, and its absence is what makes clean paving read as new.
      for (let z = -88; z < 88; z += 6.5) {
        if (rng() < 0.06 || junction(z)) continue;
        this.B.add('grime', xf(ribbonGeo(2.6 + rng() * 1.3, 7 + rng() * 3, 6.0),
          s * (12.7 + (rng() - 0.5) * 1.6), TOP + 0.026, z + 3.5, 0));
      }
      // A second, tighter pass hard against the kerb, where the run-off sits.
      for (let z = -88; z < 88; z += 9) {
        if (rng() < 0.35 || junction(z)) continue;
        this.B.add('grime', xf(ribbonGeo(1.1 + rng() * 0.8, 8 + rng() * 4, 6.0),
          s * (10.2 + rng() * 0.6), TOP + 0.025, z + 4, 0));
      }

      // Damp and oil staining in the lee of the buildings.
      for (let i = 0; i < 22; i++) {
        const z = -88 + rng() * 176;
        if (junction(z)) continue;
        this.B.add('grime', xf(ribbonGeo(0.9 + rng() * 2.4, 1.4 + rng() * 3.4, 4.0),
          s * (10.4 + rng() * 5), TOP + 0.024, z, rng() * 3));
      }

      // Bollard runs. Clustered outside the shopfronts rather than marched the
      // whole length: a continuous rank would fence the pavement off from the
      // road and kill every flanking route across the kerb.
      for (const zc of [30, -6, -52, 66]) {
        for (let i = 0; i < 5; i++) {
          const z = zc + i * 2.6;
          if (junction(z)) continue;
          const bx = inner + s * (rng() - 0.5) * 0.1;
          this.B.add('gunmetal', xf(new THREE.CylinderGeometry(0.085, 0.105, 0.86, 8), bx, TOP + 0.43, z));
          this.B.add('gunmetal', xf(new THREE.SphereGeometry(0.098, 8, 6), bx, TOP + 0.86, z));
          this.B.add('concreteDark', xf(boxGeo(0.34, 0.06, 0.34, 0.5), bx, TOP + 0.03, z));
          this._proxy(0.26, 0.92, 0.26, bx, TOP + 0.46, z);
        }
      }

      // Clutter against the plinth: crates, drums, a bin, stacked tyres. These
      // give the pavement lane its own cover so it is a route, not a verge.
      const clutter = [
        [14, 'crate'], [-20, 'drum'], [46, 'crate'], [-40, 'bin'],
        [58, 'tyre'], [-72, 'crate'], [-30, 'bin'], [76, 'drum'],
      ];
      for (const [z, kind] of clutter) {
        if (junction(z)) continue;
        const px = outer - s * rng() * 1.2;
        if (kind === 'crate') this._crateStack(px, z, rng() * 3, 1 + ((rng() * 3) | 0), rng, TOP);
        else if (kind === 'drum') {
          this._drum(px, z, ['metalRed', 'metalBlue', 'metalGreen'][(rng() * 3) | 0], rng() > 0.7, TOP);
          this._drum(px - s * 0.75, z + 0.8, 'metalYellow', false, TOP);
        } else if (kind === 'tyre') this._tyreStack(px, z, 3 + ((rng() * 2) | 0), TOP);
        else this._streetBin(px, z, rng() * 3);
      }
    }
  }

  /** Overflowing municipal bin: waist-high pavement cover with a story. */
  _streetBin(x, z, ry) {
    const g = [];
    g.push(new THREE.CylinderGeometry(0.36, 0.30, 1.0, 12));
    g.push(xf(new THREE.CylinderGeometry(0.39, 0.39, 0.07, 12), 0, 0.5, 0));
    for (const s of [-1, 1]) g.push(xf(boxGeo(0.07, 1.15, 0.07, 0.4), s * 0.38, 0.05, 0));
    this.B.add('metalGreen', xf(mergeGeometries(g, false), x, 0.75, z, ry));
    const rng = makeRng(((x * 53 + z * 19) | 0) + 7);
    for (let i = 0; i < 6; i++) {
      this.B.add(['fabricCream', 'fabricBlue', 'wood'][(rng() * 3) | 0],
        xf(boxGeo(0.16 + rng() * 0.2, 0.1, 0.14 + rng() * 0.16, 0.4),
          x + (rng() - 0.5) * 1.5, 0.3 + rng() * 1.05, z + (rng() - 0.5) * 1.5,
          rng() * 3, (rng() - 0.5) * 1.2, (rng() - 0.5) * 1.2));
    }
    this._proxy(0.8, 1.3, 0.8, x, 0.9, z);
    this.coverPoints.push(new THREE.Vector3(x, 0, z));
  }

  _buildSidewalks() {
    const rng = makeRng(5150);
    const SW = 6.6;     // sidewalk depth
    const TOP = 0.24;

    // [centre x, ±1 outward, z0, z1] runs flanking the main street.
    const runs = [
      [-9.6 - SW / 2, -1, -92, 92],
      [9.6 + SW / 2, 1, -92, 92],
    ];

    // The cross street cuts the pavement: leave the junction open.
    const junction = (a, b) => b > -22.8 && a < -5.2;

    for (const [cx, dir, z0, z1] of runs) {
      // Paving panels with joints and slight height variation.
      for (let z = z0; z < z1; z += 3.2) {
        const len = Math.min(3.1, z1 - z);
        if (junction(z, z + len)) continue;
        const h = TOP + (rng() - 0.5) * 0.02;
        // Mixed paving batches: a pavement laid in one value is the surest sign
        // of a tiled texture. A minority of darker and paler bays breaks the
        // whole run up at the range where the tile pattern would otherwise show.
        const r = rng();
        const key = r < 0.14 ? 'concreteDark' : r < 0.26 ? 'kerb' : 'tile';
        this.C.add(key, xf(boxGeo(SW + 0.2, h, len, 2.4), cx - dir * 0.1, h / 2, z + len / 2));
      }
      // Kerb stone + chamfer, segmented so the top edge catches light in bits.
      const kx = cx - dir * (SW / 2);
      for (let z = z0; z < z1; z += 2.4) {
        const len = Math.min(2.32, z1 - z);
        if (junction(z, z + len)) continue;
        const h = TOP + 0.03 + (rng() - 0.5) * 0.025;
        this.C.add('kerb', xf(boxGeo(0.4, h, len, 1.4), kx, h / 2, z + len / 2));
        this.B.add('kerb', xf(boxGeo(0.11, 0.11, len, 1.0), kx - dir * 0.2, h - 0.03, z + len / 2, 0, 0, Math.PI / 4));
      }
    }

    // Cross-street sidewalks (shorter, they only need to read at the junction).
    for (const [zc, dir] of [[-5.6 - SW / 2 + 0.4, 1], [-22.4 + SW / 2 - 0.4, -1]]) {
      for (const [x0, x1] of [[-52, -9.8], [9.8, 52]]) {
        for (let x = x0; x < x1; x += 3.2) {
          const len = Math.min(3.1, x1 - x);
          this.C.add('tile', xf(boxGeo(len, 0.24, SW + 0.2, 2.4), x + len / 2, 0.12, zc + dir * 0.1));
        }
        const kz = zc + dir * (SW / 2);
        for (let x = x0; x < x1; x += 2.4) {
          const len = Math.min(2.32, x1 - x);
          this.C.add('kerb', xf(boxGeo(len, 0.27, 0.4, 1.4), x + len / 2, 0.135, kz));
        }
      }
    }
  }

  /**
   * A ribbon decal laid down the main carriageway, its vertices dropped onto
   * the road camber so it neither floats (which would cast a shadow seam) nor
   * sinks into the asphalt at the gutter.
   */
  _roadBand(key, xc, w, z0, z1, vTile = 3, uTile = 0) {
    const g = ribbonGeo(w, z1 - z0, vTile, uTile);
    const pos = g.attributes.position;
    const cz = (z0 + z1) / 2;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i) + xc;
      pos.setXYZ(i, x, 0.19 - 0.19 * Math.min(1, (Math.abs(x) / 9.6) ** 2) + 0.007,
        pos.getZ(i) + cz);
    }
    g.computeVertexNormals();
    this.B.add(key, g);
  }

  _buildRoadDressing() {
    const rng = makeRng(31337);
    const roadY = (x) => 0.19 - 0.19 * Math.min(1, (Math.abs(x) / 9.6) ** 2);

    /* --- wheel-track polish pass -----------------------------------------
       A tiled asphalt material cannot know where the traffic runs, so the two
       tyre paths per lane are laid in world space as a decal ribbon: swept,
       burnished asphalt inside the track and a pale dust film everywhere else.
       The dust is what actually reads at distance; the polish is what catches
       the low sun when you are stood in the lane.                          */

    // Lane centres, then a 1.68 m track gauge either side of each.
    const LANES = [-5.1, 5.1];
    const GAUGE = 0.84;
    for (const lc of LANES) {
      for (const s of [-1, 1]) {
        const x0 = lc + s * GAUGE;
        // The pair wanders together — a vehicle does not steer its wheels apart.
        for (let z = -90; z < 90; z += 9) {
          if (rng() < 0.10) continue;
          const len = 8.4 + rng() * 1.2;
          const wob = Math.sin((z + len / 2) * 0.032 + lc) * 0.55;
          this._roadBand('polish', x0 + wob, 0.66 + rng() * 0.12, z, z + len, 2.4, 2.4);
        }
      }
      // Faint smear between the wheels where the sump drips and the mud drops.
      for (let z = -90; z < 90; z += 22) {
        this._roadBand('scorch', lc + Math.sin(z * 0.032 + lc) * 0.55, 0.55,
          z, z + 14 + rng() * 6, 3.0, 3.0);
      }
    }

    // Dust film. Traffic sweeps a carriageway clean, so the grit banks up in the
    // gutters and only drifts across the crown here and there — the contrast
    // between swept and unswept is what makes the wheel paths legible, and it
    // dies the moment the dust becomes a carpet.
    for (const [xc, w] of [[-8.6, 2.3], [8.6, 2.3]]) {
      for (let z = -90; z < 90; z += 15) {
        if (rng() < 0.15) continue;
        this._roadBand('dust', xc + (rng() - 0.5) * 0.6, w * (0.7 + rng() * 0.6),
          z, z + 12 + rng() * 4, 5.0);
      }
    }
    // Occasional drift blown out across the lanes, anchored on a gutter.
    for (let i = 0; i < 9; i++) {
      const s = rng() > 0.5 ? 1 : -1;
      const z = -84 + rng() * 168;
      this._roadBand('dust', s * (5.4 + rng() * 2.4), 1.1 + rng() * 1.4,
        z, z + 5 + rng() * 7, 5.0);
    }

    // Cross-street tracks and dust, same construction rotated a quarter turn.
    for (const [xa, xb] of [[-60, -10], [10, 60]]) {
      for (const zc of [-16.6, -11.4]) {
        for (const s of [-1, 1]) {
          for (let x = xa; x < xb; x += 9) {
            if (rng() < 0.16) continue;
            const len = 8.2 + rng();
            this.B.add('polish', xf(ribbonGeo(0.66, len, 2.4, 2.4),
              x + len / 2, 0.176, zc + s * 0.84, Math.PI / 2));
          }
        }
      }
      for (const zc of [-21.0, -7.0]) {
        for (let x = xa; x < xb; x += 15) {
          if (rng() < 0.2) continue;
          this.B.add('dust', xf(ribbonGeo(2.0, 13, 5.0), x + 6.5, 0.170, zc, Math.PI / 2));
        }
      }
    }

    // Grit banked against the building plinths, and the odd blow across the
    // paving. Nothing welds a building to the ground like dirt in the joint
    // between the two; a clean pavement line always reads as a kit-bash.
    for (const s of [-1, 1]) {
      for (let z = -90; z < 90; z += 13) {
        if (rng() < 0.18) continue;
        this.B.add('dust', xf(ribbonGeo(1.5 + rng() * 1.1, 11 + rng() * 4, 5.0),
          s * (15.5 - rng() * 0.8), 0.272, z + 6, 0));
      }
      for (let i = 0; i < 7; i++) {
        const z = -88 + rng() * 176;
        this.B.add('dust', xf(ribbonGeo(1.0 + rng() * 1.2, 3.5 + rng() * 4, 5.0),
          s * (10.6 + rng() * 4.5), 0.271, z, rng() * 0.5 - 0.25));
      }
    }

    // Worn centre line: dashes drop out, lengths vary, some are ghosts.
    for (let z = -88; z < 88; z += 5.5) {
      if (rng() < 0.22) continue;
      const len = 2.2 + rng() * 0.9;
      const m = rng() < 0.35 ? 'patch' : 'paint';
      this.B.add(m, xf(planeGeo(0.18 + rng() * 0.06, len), 0, roadY(0) + 0.012, z, 0, -Math.PI / 2));
    }
    // Gutter edge lines, heavily eroded.
    for (const x of [-8.4, 8.4]) {
      for (let z = -88; z < 88; z += 3.2) {
        if (rng() < 0.45) continue;
        this.B.add('paint', xf(planeGeo(0.14, 2.4 + rng()), x, roadY(x) + 0.012, z, 0, -Math.PI / 2));
      }
    }
    // Zebra crossing north of the junction, half scrubbed off.
    for (let i = 0; i < 12; i++) {
      if (rng() < 0.3) continue;
      const x = -8.2 + i * 1.5;
      this.B.add(rng() < 0.4 ? 'patch' : 'paint',
        xf(planeGeo(0.72, 3.6), x, roadY(x) + 0.013, 2.5, 0, -Math.PI / 2));
    }

    // Repair patches and potholes.
    for (let i = 0; i < 26; i++) {
      const x = (rng() - 0.5) * 17;
      const z = (rng() - 0.5) * 170;
      const w = 1.4 + rng() * 3.6, l = 1.4 + rng() * 4.2;
      this.B.add('patch', xf(planeGeo(w, l), x, roadY(x) + 0.008, z, rng() * Math.PI, -Math.PI / 2));
    }

    // Manholes: a recessed collar with a proud lid.
    const holes = [[2.6, 26], [-3.4, -6], [4.2, -44], [-5.1, 52], [1.4, -70], [26, 8], [-38, -22]];
    for (const [x, z] of holes) {
      const y = Math.abs(x) < 9.6 ? roadY(x) : 0.02;
      this.B.add('concreteDark', xf(new THREE.CylinderGeometry(0.78, 0.78, 0.05, 18), x, y + 0.01, z));
      const lid = new THREE.CylinderGeometry(0.62, 0.64, 0.07, 20);
      this.B.add('metal', xf(lid, x, y + 0.035, z));
      for (let r = 0; r < 4; r++) {
        this.B.add('metal', xf(boxGeo(0.9, 0.03, 0.07, 0.6), x, y + 0.075, z, r * 0.4 + 0.2));
      }
    }

    // Gutter drainage grates — bar geometry, not a texture.
    for (const [x, z] of [[-8.9, 14], [8.9, -2], [-8.9, -34], [8.9, 40], [-8.9, 62], [8.9, -60]]) {
      this.B.add('concreteDark', xf(boxGeo(1.0, 0.12, 1.5, 1.2), x, 0.06, z));
      for (let i = 0; i < 7; i++) {
        this.B.add('gunmetal', xf(boxGeo(0.82, 0.05, 0.09, 0.5), x, 0.135, z - 0.6 + i * 0.2));
      }
    }

    // Standing water — a mirror-smooth decal is what sells "wet street".
    const puddles = [[-8.2, 18, 2.6], [8.6, -18, 2.2], [-8.6, -50, 3.0], [8.2, 46, 1.9],
      [-2.0, -12, 3.4], [3.5, 60, 1.7], [-8.8, 74, 2.0], [24, 12, 2.2], [-38, -30, 2.6],
      [-8.4, 6, 2.2], [8.8, 30, 2.0], [-8.5, -66, 2.4]];
    for (const [x, z, r] of puddles) {
      const g = new THREE.CircleGeometry(r * 0.5, 26);
      const pos = g.attributes.position;
      for (let i = 1; i < pos.count; i++) {
        const s = 0.45 + rng() * 0.8;
        pos.setXY(i, pos.getX(i) * s * 1.5, pos.getY(i) * s);
      }
      g.computeVertexNormals();
      const y = Math.abs(x) < 9.6 ? roadY(x) : 0.03;
      this.B.add('puddle', xf(g, x, y + 0.006, z, rng() * 3, -Math.PI / 2));
    }
  }

  /* --------------------------------------------------------- buildings --- */

  _buildBlocks() {
    const B = [
      // West row (facades at x = -16.2 looking east over the street). Heights
      // step 5-2-6-3 so the roofline reads as a skyline, not a ruler.
      { x: -28, z: 34, w: 22, d: 24, floors: 5, mat: 'plasterWarm', front: [2], awning: true, balconies: true },
      { x: -28, z: 7, w: 22, d: 20, floors: 2, mat: 'brick', front: [2], awning: true, shop: true },
      { x: -28, z: -32, w: 22, d: 22, floors: 6, mat: 'plasterSand', front: [2], balconies: true, damaged: true },
      { x: -28, z: -66, w: 22, d: 26, floors: 3, mat: 'concrete', front: [2], setback: true },
      // East row (facades at x = +16.2)
      { x: 31, z: 44, w: 26, d: 22, floors: 4, mat: 'plasterPale', front: [3], awning: true, shop: true },
      { x: 30, z: 10, w: 24, d: 26, floors: 6, mat: 'plasterWarm', front: [3], balconies: true, fireEscape: true },
      { x: 32, z: -33, w: 28, d: 26, floors: 3, mat: 'brick', front: [3], balconies: true, awning: true },
      { x: 34, z: -68, w: 30, d: 24, floors: 7, mat: 'concrete', front: [3], setback: true },
      // Street caps — the north one is tall enough to close the view.
      { x: -4, z: 74, w: 40, d: 22, floors: 6, mat: 'plasterSand', front: [1], awning: true },
      { x: 6, z: -84, w: 34, d: 20, floors: 3, mat: 'plasterCool', front: [0] },
      // Second rank, seen over the rooftops: silhouette only.
      { x: -64, z: 22, w: 22, d: 22, floors: 8, mat: 'plasterCool', front: [2] },
      { x: -66, z: -30, w: 24, d: 24, floors: 4, mat: 'plasterPale', front: [2], setback: true },
      { x: 64, z: 24, w: 24, d: 24, floors: 5, mat: 'plasterSand', front: [] },
      { x: 68, z: -18, w: 22, d: 22, floors: 9, mat: 'concrete', front: [] },
      { x: -64, z: 62, w: 26, d: 20, floors: 6, mat: 'plasterTeal', front: [] },
      { x: 62, z: -60, w: 26, d: 22, floors: 5, mat: 'plasterWarm', front: [] },
    ];
    for (let i = 0; i < B.length; i++) this._building({ seed: 100 + i * 17, ...B[i] });

    // Single-storey annex on the east: its roof is the map's elevation play.
    this._annex();

    // Perimeter: a blast wall rather than a bare box, so the edge reads as art.
    this._perimeter();
  }

  _building(cfg) {
    const {
      x, z, w, d, floors, mat, seed,
      front = [], balconies = false, fireEscape = false, awning = false,
      shop = false, damaged = false, setback = false,
    } = cfg;
    const rng = makeRng(seed);
    const G0 = 4.6, FH = 3.2;
    const h = G0 + (floors - 1) * FH;

    // Solid collision volume at the outer facade plane.
    this._proxy(w, h + 2.2, d, x, (h + 2.2) / 2, z);

    // Dark structural core: this is what you see through the window reveals.
    const core = new THREE.Mesh(boxGeo(w - WALL_T * 2, h, d - WALL_T * 2, 3), this.mat.interior);
    core.position.set(x, h / 2, z);
    this._add(core, false);

    const dmg = damaged ? { x0: w / 2 - 9, x1: w / 2 + 1, y0: 3.4, y1: h - 1.0, face: 2 } : null;

    for (let f = 0; f < 4; f++) {
      this._facade({ ...cfg, h, G0, FH, rng, face: f, dmg: dmg && dmg.face === f ? dmg : null,
        detail: front.includes(f) });
    }

    // Roof deck + mechanical plant.
    this.B.add('concreteDark', xf(boxGeo(w - WALL_T * 2 + 0.1, 0.28, d - WALL_T * 2 + 0.1, 3), x, h + 0.14, z));
    this._roofPlant(x, z, w, d, h, rng, mat);

    if (setback) {
      const sw = w - 8, sd = d - 8, sh = FH * 1.6;
      this._proxy(sw, sh, sd, x, h + sh / 2, z);
      const pent = new THREE.Mesh(boxGeo(sw, sh, sd, 3), this.mat[mat]);
      pent.position.set(x, h + sh / 2, z);
      this._add(pent, false);
      this.B.add('concreteDark', xf(boxGeo(sw + 0.7, 0.3, sd + 0.7, 3), x, h + sh + 0.15, z));
      for (let f = 0; f < 4; f++) {
        const W = f < 2 ? sw : sd;
        const half = f < 2 ? sd / 2 : sw / 2;
        const M = new THREE.Matrix4().makeTranslation(x, 0, z)
          .multiply(_m4b.makeRotationY(FACE_RY[f]))
          .multiply(new THREE.Matrix4().makeTranslation(0, 0, half));
        for (let i = 0; i < 3; i++) {
          const cx = -W / 2 + (W / 3) * (i + 0.5);
          this._windowUnit((k, g) => this.B.add(k, g.applyMatrix4(M)), cx, h + 1.4, 1.3, 1.6, rng, mat);
        }
      }
    }

    if (fireEscape) this._fireEscape(cfg, h, G0, FH);
    if (awning) this._shopAwnings(cfg, rng);
  }

  /**
   * One face of cladding. The wall is assembled as alternating solid spandrel
   * bands and pier bands, which leaves genuine holes at the windows.
   */
  _facade(cfg) {
    const { x, z, w, d, floors, mat, h, G0, FH, rng, face, dmg, detail, shop } = cfg;
    const W = face < 2 ? w : d;
    const half = face < 2 ? d / 2 : w / 2;
    const M = new THREE.Matrix4().makeTranslation(x, 0, z)
      .multiply(_m4b.makeRotationY(FACE_RY[face]))
      .multiply(new THREE.Matrix4().makeTranslation(0, 0, half));
    // Every building gets its own UV phase — and every face its own on top of
    // that — so no two walls in a run ever line their spalling up. The mirror
    // doubles the number of distinct-looking facades for free.
    const du = ((x * 0.37 + z * 0.11 + face * 1.63) % 7) + 7;
    const dv = ((z * 0.29 + x * 0.07 + face * 0.91) % 7) + 7;
    const flip = (Math.round(x * 0.5 + z * 0.5) + face) % 2 ? -1 : 1;
    const A = (key, geo) => this.B.add(key, UV01_KEYS.has(key)
      ? geo.applyMatrix4(M)
      : uvShift(geo.applyMatrix4(M), du, dv, flip));
    const T = WALL_T;

    const cols = Math.max(2, Math.round((W - 2.0) / 3.2));
    const pitch = (W - 2.0) / cols;
    const winW = Math.min(1.55, pitch - 1.1);
    const colX = (i) => -W / 2 + 1.0 + pitch * (i + 0.5);

    const cut = (x0, x1, y0, y1) =>
      dmg && x1 > dmg.x0 && x0 < dmg.x1 && y1 > dmg.y0 && y0 < dmg.y1;

    // Solid band across the full width, minus any blown-out span.
    const band = (y0, y1) => {
      if (y1 - y0 < 0.02) return;
      const segs = [[-W / 2, W / 2]];
      if (dmg && y1 > dmg.y0 && y0 < dmg.y1) {
        segs.length = 0;
        if (dmg.x0 > -W / 2) segs.push([-W / 2, Math.min(dmg.x0, W / 2)]);
        if (dmg.x1 < W / 2) segs.push([Math.max(dmg.x1, -W / 2), W / 2]);
      }
      for (const [a, b] of segs) {
        if (b - a < 0.05) continue;
        A(mat, xf(boxGeo(b - a, y1 - y0, T, 6.5), (a + b) / 2, (y0 + y1) / 2, -T / 2));
      }
    };

    // Pier band: solid between the openings.
    const piers = (y0, y1, openings) => {
      let cursor = -W / 2;
      for (const [ox, ow] of openings) {
        const a = cursor, b = ox - ow / 2;
        if (b - a > 0.05 && !cut(a, b, y0, y1)) {
          A(mat, xf(boxGeo(b - a, y1 - y0, T, 6.5), (a + b) / 2, (y0 + y1) / 2, -T / 2));
        }
        cursor = ox + ow / 2;
      }
      if (W / 2 - cursor > 0.05 && !cut(cursor, W / 2, y0, y1)) {
        A(mat, xf(boxGeo(W / 2 - cursor, y1 - y0, T, 6.5), (cursor + W / 2) / 2, (y0 + y1) / 2, -T / 2));
      }
    };

    let cursor = 0;
    for (let f = 0; f < floors; f++) {
      const base = f === 0 ? 0 : G0 + (f - 1) * FH;
      const isGround = f === 0;
      const yb = base + (isGround ? 0.95 : 1.05);
      const oh = isGround ? 2.55 : 1.75;
      const ow = isGround ? Math.min(pitch - 0.75, 2.6) : winW;
      const openings = [];
      for (let i = 0; i < cols; i++) openings.push([colX(i), ow]);
      // A doorway on detailed ground floors: full-height opening in one bay.
      const doorBay = isGround && detail ? 1 + ((rng() * (cols - 1)) | 0) : -1;

      band(cursor, yb);
      piers(yb, yb + oh, openings);
      cursor = yb + oh;

      for (let i = 0; i < cols; i++) {
        const cx = colX(i);
        if (cut(cx - ow / 2, cx + ow / 2, yb, yb + oh)) continue;
        if (i === doorBay) {
          // Doorway: knock the sill band out and drop in a recessed door.
          A(mat, xf(boxGeo(ow, yb, T - 0.35, 6.5), cx, yb / 2, -T + 0.175));
          A('wood', xf(boxGeo(ow - 0.3, yb - 0.1, 0.12, 1.2), cx, (yb - 0.1) / 2, -T + 0.42));
          A('concreteDark', xf(boxGeo(ow + 0.5, 0.24, 0.5, 1.4), cx, yb + oh + 0.12, 0.06));
          continue;
        }
        this._windowUnit(A, cx, yb + oh / 2, ow, oh, rng, mat, isGround && (shop || detail));
      }

      // Cornice at each floor line; heavier one over the shopfront.
      const cy = f === 0 ? G0 - 0.35 : base + FH - 0.35;
      A('concreteDark', xf(boxGeo(W + 0.5, isGround ? 0.42 : 0.26, isGround ? 0.55 : 0.36, 1.6),
        0, cy, isGround ? 0.16 : 0.1));
    }
    band(cursor, h);

    // Plinth, corner pilasters.
    A('concreteDark', xf(boxGeo(W + 0.5, 1.05, 0.42, 1.6), 0, 0.52, 0.12));
    for (const s of [-1, 1]) {
      A('concreteDark', xf(boxGeo(0.62, h - 0.4, 0.34, 2.0), s * (W / 2 - 0.31), (h + 0.4) / 2, 0.1));
    }

    // Parapet ring + coping.
    A(mat, xf(boxGeo(W + 0.2, 1.15, 0.5, 6.5), 0, h + 0.58, -0.24));
    A('concreteDark', xf(boxGeo(W + 0.7, 0.18, 0.72, 1.6), 0, h + 1.24, -0.16));

    if (detail) this._facadeDetail(A, cfg, W, cols, pitch, colX, rng);
    if (dmg) this._facadeDamage(A, cfg, W, dmg, rng, M);
  }

  /** Frame, mullions, glass, and the boarded / broken / curtained variants. */
  _windowUnit(A, cx, cy, ow, oh, rng, mat, isShop = false) {
    const T = WALL_T;
    const roll = rng();

    // Sill and lintel — the strongest shadow-catchers on any facade.
    A('concreteDark', xf(boxGeo(ow + 0.42, 0.14, 0.5, 1.4), cx, cy - oh / 2 - 0.07, 0.1));
    A('concreteDark', xf(boxGeo(ow + 0.42, 0.06, 0.36, 1.4), cx, cy - oh / 2 - 0.17, 0.14, 0, 0.2));
    A('concreteDark', xf(boxGeo(ow + 0.5, 0.2, 0.32, 1.4), cx, cy + oh / 2 + 0.1, 0.04));

    // Timber frame set back in the reveal.
    const fz = -0.14;
    const fw = 0.09;
    A('wood', xf(boxGeo(ow, fw, 0.14, 0.9), cx, cy + oh / 2 - fw / 2, fz));
    A('wood', xf(boxGeo(ow, fw, 0.14, 0.9), cx, cy - oh / 2 + fw / 2, fz));
    for (const s of [-1, 1]) {
      A('wood', xf(boxGeo(fw, oh, 0.14, 0.9), cx + s * (ow / 2 - fw / 2), cy, fz));
    }
    // Mullion + transom.
    A('wood', xf(boxGeo(0.07, oh - fw * 2, 0.12, 0.9), cx, cy, fz - 0.01));
    if (!isShop) A('wood', xf(boxGeo(ow - fw * 2, 0.06, 0.12, 0.9), cx, cy + oh * 0.16, fz - 0.01));

    if (roll < 0.14) {
      // Boarded up.
      for (let p = 0; p < 4; p++) {
        const ang = (rng() - 0.5) * 0.28;
        const yy = cy - oh / 2 + 0.35 + p * (oh - 0.7) / 3;
        A('wood', xf(boxGeo(ow + 0.28, 0.26, 0.05, 1.0), cx, yy, -0.05, 0, 0, ang));
      }
      A('glass', xf(planeGeo(ow - 0.16, oh - 0.16), cx, cy, -0.2));
    } else if (roll < 0.30) {
      // Blown out: shards clinging to the frame.
      for (let s = 0; s < 5; s++) {
        const g = new THREE.BufferGeometry();
        const top = rng() > 0.4;
        const x0 = -ow / 2 + 0.1 + rng() * (ow - 0.2);
        const wdt = 0.16 + rng() * 0.3;
        const depth = 0.15 + rng() * 0.5;
        const yEdge = top ? oh / 2 - 0.1 : -oh / 2 + 0.1;
        const dir = top ? -1 : 1;
        const verts = new Float32Array([
          x0, yEdge, 0, x0 + wdt, yEdge, 0, x0 + wdt * (0.3 + rng() * 0.5), yEdge + dir * depth, 0,
        ]);
        g.setAttribute('position', new THREE.BufferAttribute(verts, 3));
        g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(6), 2));
        g.computeVertexNormals();
        A('glassBroken', xf(g, cx, cy, -0.19));
      }
    } else {
      // Shopfronts get a shallow interior in the 0.66 m of reveal behind the
      // glass: a back card, shelving and stock. Without it a run of shops is a
      // row of identical dark blue mirrors, which is the single most obvious
      // tell that a facade is a texture rather than a building.
      if (isShop) {
        A('interiorWarm', xf(planeGeo(ow, oh, 2.0), cx, cy, -T + 0.06));
        const shelves = 2 + ((rng() * 2) | 0);
        for (let s = 0; s < shelves; s++) {
          const sy = cy - oh / 2 + 0.45 + s * ((oh - 0.7) / shelves);
          A('wood', xf(boxGeo(ow - 0.2, 0.05, 0.34, 0.8), cx, sy, -T + 0.24));
          const n = 2 + ((rng() * 3) | 0);
          for (let k = 0; k < n; k++) {
            const bw = 0.12 + rng() * 0.18;
            A(['fabricRed', 'fabricBlue', 'fabricCream', 'wood'][(rng() * 4) | 0],
              xf(boxGeo(bw, 0.14 + rng() * 0.18, 0.16, 0.4),
                cx - (ow - 0.4) / 2 + rng() * (ow - 0.4), sy + 0.12, -T + 0.24));
          }
        }
        A('gunmetal', xf(boxGeo(ow - 0.5, 0.06, 0.3, 0.6), cx, cy + oh / 2 - 0.2, -T + 0.5));
      }
      A(isShop ? 'glassShop' : 'glass', xf(planeGeo(ow - 0.16, oh - 0.16), cx, cy, -0.19));
      if (roll < 0.48 && !isShop) {
        // Curtain hanging in the reveal — pushes the interior back visually.
        const cw = (ow - 0.2) * (0.45 + rng() * 0.5);
        const g = planeGeo(cw, oh - 0.22, 0);
        const pos = g.attributes.position;
        for (let i = 0; i < pos.count; i++) {
          pos.setZ(i, Math.sin(pos.getX(i) * 6 + rng() * 0.2) * 0.05);
        }
        g.computeVertexNormals();
        A(rng() > 0.5 ? 'fabricCream' : 'fabricRed',
          xf(g, cx + (rng() > 0.5 ? 1 : -1) * (ow / 2 - cw / 2 - 0.1), cy, -T + 0.28));
      }
    }
  }

  _facadeDetail(A, cfg, W, cols, pitch, colX, rng) {
    const { h, G0, FH, floors, balconies } = cfg;

    // Downpipes with collars, hard against the pilasters.
    for (const s of [-1, 1]) {
      const px = s * (W / 2 - 0.75);
      A('metal', xf(new THREE.CylinderGeometry(0.1, 0.1, h - 0.2, 8), px, (h - 0.2) / 2, 0.28));
      for (let y = 1.4; y < h - 1; y += 2.6) {
        A('gunmetal', xf(boxGeo(0.3, 0.08, 0.36, 0.5), px, y, 0.2));
      }
      A('metal', xf(new THREE.CylinderGeometry(0.11, 0.13, 0.5, 8), px, 0.3, 0.5, 0, 0.5));
    }

    // Conduit run above the shopfront with drops to the ground.
    const cy = G0 - 0.9;
    A('gunmetal', xf(new THREE.CylinderGeometry(0.05, 0.05, W - 2.2, 6), 0, cy, 0.22, 0, 0, Math.PI / 2));
    for (let i = 0; i < 3; i++) {
      const dx = -W / 3 + i * (W / 3);
      A('gunmetal', xf(new THREE.CylinderGeometry(0.045, 0.045, cy - 0.3, 6), dx, (cy - 0.3) / 2 + 0.3, 0.22));
      A('gunmetal', xf(boxGeo(0.26, 0.34, 0.16, 0.4), dx, cy + 0.5, 0.16));
    }

    // Air-conditioners bolted under a scatter of windows.
    for (let f = 1; f < floors; f++) {
      for (let i = 0; i < cols; i++) {
        if (rng() > 0.3) continue;
        const y = G0 + (f - 1) * FH + 1.0;
        const cxp = colX(i) + (rng() - 0.5) * 0.5;
        A('gunmetal', xf(boxGeo(0.86, 0.56, 0.44, 0.7), cxp, y, 0.35));
        A('metal', xf(new THREE.CylinderGeometry(0.19, 0.19, 0.06, 12), cxp, y, 0.58, 0, Math.PI / 2));
        A('gunmetal', xf(boxGeo(0.1, 0.12, 0.34, 0.4), cxp - 0.36, y - 0.34, 0.2));
        A('gunmetal', xf(boxGeo(0.1, 0.12, 0.34, 0.4), cxp + 0.36, y - 0.34, 0.2));
      }
    }

    // Balconies: slab, apron, railing with balusters.
    if (balconies) {
      for (let f = 1; f < floors; f++) {
        for (let i = 0; i < cols; i++) {
          if (rng() > 0.42) continue;
          const y = G0 + (f - 1) * FH + 0.1;
          const bx = colX(i);
          const bw = Math.min(pitch * 1.25, 3.4);
          A('concreteDark', xf(boxGeo(bw, 0.2, 1.35, 1.6), bx, y, 0.62));
          A('concreteDark', xf(boxGeo(bw + 0.12, 0.1, 1.45, 1.2), bx, y - 0.14, 0.62));
          for (const [ox, oz, ow2, od] of [[0, 1.24, bw, 0.07], [-bw / 2, 0.62, 0.07, 1.3], [bw / 2, 0.62, 0.07, 1.3]]) {
            A('gunmetal', xf(boxGeo(ow2, 0.06, od, 0.5), bx + ox, y + 1.02, oz));
            A('gunmetal', xf(boxGeo(ow2, 0.05, od, 0.5), bx + ox, y + 0.55, oz));
          }
          const n = Math.max(4, Math.round(bw / 0.28));
          for (let b = 0; b <= n; b++) {
            A('gunmetal', xf(boxGeo(0.035, 1.0, 0.035, 0.4), bx - bw / 2 + (bw * b) / n, y + 0.6, 1.24));
          }
          for (let b = 0; b < 4; b++) {
            for (const s of [-1, 1]) {
              A('gunmetal', xf(boxGeo(0.035, 1.0, 0.035, 0.4), bx + s * bw / 2, y + 0.6, 0.05 + b * 0.4));
            }
          }
          // Laundry on some of them.
          if (rng() > 0.55) {
            for (let c = 0; c < 3; c++) {
              const cw = 0.4 + rng() * 0.3;
              A(rng() > 0.5 ? 'fabricBlue' : 'fabricCream',
                xf(planeGeo(cw, 0.6 + rng() * 0.4), bx - bw / 3 + c * (bw / 3), y + 0.68, 1.22));
            }
          }
        }
      }
    }

    // Paper on the plinth: posters and a graffiti tag.
    for (let i = 0; i < 4; i++) {
      if (rng() > 0.6) continue;
      const px = -W / 2 + 1 + rng() * (W - 2);
      const key = 'poster' + ((rng() * 3) | 0);
      A(key, xf(planeGeo(0.9, 1.35), px, 1.5 + rng() * 0.5, 0.03, 0, 0, (rng() - 0.5) * 0.1));
    }
    if (rng() > 0.35) {
      A('graffiti' + ((rng() * 2) | 0),
        xf(planeGeo(3.4, 1.7), (rng() - 0.5) * (W - 6), 1.6, 0.04));
    }
  }

  /** Blown-open corner: floor slabs, hanging rebar, rubble spill. */
  _facadeDamage(A, cfg, W, dmg, rng, M) {
    const { G0, FH, floors, x, z, w, d, face } = cfg;
    for (let f = 1; f < floors; f++) {
      const y = G0 + (f - 1) * FH;
      if (y < dmg.y0 || y > dmg.y1) continue;
      const len = (dmg.x1 - dmg.x0) * (0.4 + rng() * 0.5);
      A('concreteDark', xf(boxGeo(len, 0.26, 1.4, 1.6), dmg.x0 + len / 2, y, -0.7, 0, (rng() - 0.5) * 0.12));
      for (let r = 0; r < 5; r++) {
        A('gunmetal', xf(new THREE.CylinderGeometry(0.022, 0.022, 0.5 + rng() * 0.8, 5),
          dmg.x0 + rng() * len, y - 0.3, -0.4 - rng() * 0.5, 0, 0, (rng() - 0.5) * 1.4));
      }
    }
    // Jagged plaster teeth around the edge of the hole.
    for (let i = 0; i < 14; i++) {
      const t = rng();
      const px = dmg.x0 + t * (dmg.x1 - dmg.x0);
      const py = rng() > 0.5 ? dmg.y0 : dmg.y1;
      A(cfg.mat, xf(boxGeo(0.5 + rng() * 0.9, 0.4 + rng() * 0.7, WALL_T, 2.0),
        px, py + (rng() - 0.5) * 0.7, -WALL_T / 2, 0, 0, (rng() - 0.5) * 0.5));
    }
  }

  _roofPlant(x, z, w, d, h, rng, mat) {
    const top = h + 0.3;
    const iw = w - WALL_T * 2 - 1.5, id = d - WALL_T * 2 - 1.5;
    const rp = (fx, fz) => [x + (fx - 0.5) * iw, z + (fz - 0.5) * id];

    // Chiller / plant boxes on a plinth.
    for (let i = 0; i < 2 + ((rng() * 3) | 0); i++) {
      const [px, pz] = rp(rng(), rng());
      const bw = 1.4 + rng() * 1.8, bd = 1.0 + rng() * 1.4, bh = 0.9 + rng() * 0.9;
      this.B.add('concreteDark', xf(boxGeo(bw + 0.4, 0.18, bd + 0.4, 1.4), px, top + 0.09, pz));
      this.B.add('gunmetal', xf(boxGeo(bw, bh, bd, 1.2), px, top + 0.18 + bh / 2, pz, rng() * 0.6));
      for (let v = 0; v < 3; v++) {
        this.B.add('metal', xf(new THREE.CylinderGeometry(0.28, 0.28, 0.1, 12),
          px - bw / 3 + v * (bw / 3), top + 0.18 + bh + 0.05, pz, rng()));
      }
    }
    // Water tanks.
    for (let i = 0; i < 1 + ((rng() * 3) | 0); i++) {
      const [px, pz] = rp(rng(), rng());
      const r = 0.55 + rng() * 0.35, th = 1.1 + rng() * 0.6;
      for (const [ox, oz] of [[-r, -r], [r, -r], [-r, r], [r, r]]) {
        this.B.add('gunmetal', xf(boxGeo(0.1, 0.5, 0.1, 0.4), px + ox * 0.8, top + 0.25, pz + oz * 0.8));
      }
      this.B.add('metal', xf(new THREE.CylinderGeometry(r, r, th, 14), px, top + 0.5 + th / 2, pz));
      this.B.add('gunmetal', xf(new THREE.CylinderGeometry(r * 0.25, r * 0.25, 0.16, 10), px, top + 0.5 + th + 0.08, pz));
    }
    // Satellite dishes.
    for (let i = 0; i < 1 + ((rng() * 3) | 0); i++) {
      const [px, pz] = rp(rng(), rng());
      const r = 0.42 + rng() * 0.3;
      const dish = new THREE.SphereGeometry(r, 14, 8, 0, Math.PI * 2, 0, 0.85);
      const yaw = rng() * Math.PI * 2;
      this.B.add('gunmetal', xf(new THREE.CylinderGeometry(0.05, 0.06, 1.1, 6), px, top + 0.55, pz));
      this.B.add('plasterPale', xf(dish, px, top + 1.2, pz, yaw, Math.PI * 0.72));
      this.B.add('gunmetal', xf(new THREE.CylinderGeometry(0.03, 0.03, r * 1.1, 5),
        px + Math.sin(yaw) * r * 0.5, top + 1.2 + r * 0.4, pz + Math.cos(yaw) * r * 0.5, yaw, 0.7));
    }
    // Antenna mast with guy wires reads at silhouette distance.
    if (rng() > 0.45) {
      const [px, pz] = rp(rng(), rng());
      const mh = 3.5 + rng() * 4;
      this.B.add('gunmetal', xf(new THREE.CylinderGeometry(0.05, 0.09, mh, 6), px, top + mh / 2, pz));
      for (let k = 0; k < 3; k++) {
        this.B.add('gunmetal', xf(boxGeo(0.9, 0.04, 0.04, 0.4), px, top + mh * (0.5 + k * 0.15), pz, k * 0.7));
      }
      for (let k = 0; k < 3; k++) {
        const a = (k / 3) * Math.PI * 2;
        const len = Math.hypot(mh * 0.8, 2.6);
        this.B.add('gunmetal', xf(new THREE.CylinderGeometry(0.015, 0.015, len, 4),
          px + Math.sin(a) * 1.3, top + mh * 0.45, pz + Math.cos(a) * 1.3, a, Math.atan2(2.6, mh * 0.8)));
      }
    }
    // Vent stacks.
    for (let i = 0; i < 3; i++) {
      const [px, pz] = rp(rng(), rng());
      this.B.add('metal', xf(new THREE.CylinderGeometry(0.12, 0.12, 0.8 + rng() * 0.7, 8), px, top + 0.6, pz));
    }
  }

  _fireEscape(cfg, h, G0, FH) {
    const { x, z, w, d, front } = cfg;
    const dir = front.includes(3) ? -1 : 1;                       // which face
    const fx = x + dir * (w / 2 + 0.1);
    const zc = z + 3.5;
    const rise = FH;
    for (let f = 1; f < Math.min(5, Math.round((h - G0) / FH) + 1); f++) {
      const y = G0 + (f - 1) * FH;
      // Landing.
      this.B.add('gunmetal', xf(boxGeo(1.5, 0.08, 3.2, 1.0), fx + dir * 0.75, y, zc));
      for (let b = 0; b < 8; b++) {
        this.B.add('gunmetal', xf(boxGeo(0.04, 1.0, 0.04, 0.4), fx + dir * 1.5, y + 0.5, zc - 1.6 + b * 0.45));
      }
      this.B.add('gunmetal', xf(boxGeo(0.06, 0.06, 3.2, 0.4), fx + dir * 1.5, y + 1.0, zc));
      // Flight down to the previous landing.
      const len = Math.hypot(rise, 2.4);
      const ang = Math.atan2(rise, 2.4);
      this.B.add('gunmetal', xf(boxGeo(1.1, 0.07, len, 1.0), fx + dir * 0.6, y - rise / 2, zc + 2.6, 0, ang));
      for (let s = 0; s < 8; s++) {
        const t = (s + 0.5) / 8;
        this.B.add('gunmetal', xf(boxGeo(1.05, 0.05, 0.16, 0.4),
          fx + dir * 0.6, y - rise + rise * t + 0.06, zc + 1.4 + 2.4 * t));
      }
    }
  }

  _shopAwnings(cfg, rng) {
    const { x, z, w, d, front } = cfg;
    for (const face of front) {
      const W = face < 2 ? w : d;
      const half = face < 2 ? d / 2 : w / 2;
      const M = new THREE.Matrix4().makeTranslation(x, 0, z)
        .multiply(_m4b.makeRotationY(FACE_RY[face]))
        .multiply(new THREE.Matrix4().makeTranslation(0, 0, half));
      const A = (key, g) => this.B.add(key, g.applyMatrix4(M));
      const n = 2 + ((rng() * 2) | 0);
      for (let i = 0; i < n; i++) {
        const cx = -W / 2 + (W / (n + 1)) * (i + 1) + (rng() - 0.5) * 1.5;
        const aw = 3.2 + rng() * 1.8;
        const y = 3.5;
        const col = ['fabricRed', 'fabricBlue', 'fabricCream'][(rng() * 3) | 0];
        A(col, xf(this._tornSheet(aw, 1.9, 9, rng, 0.16), cx, y, 1.0, 0, -0.55));
        A('gunmetal', xf(new THREE.CylinderGeometry(0.035, 0.035, aw, 5), cx, y + 0.05, 0.12, 0, 0, Math.PI / 2));
        A('gunmetal', xf(new THREE.CylinderGeometry(0.035, 0.035, aw, 5), cx, y - 0.85, 1.85, 0, 0, Math.PI / 2));
        for (const s of [-1, 1]) {
          A('gunmetal', xf(new THREE.CylinderGeometry(0.03, 0.03, 2.0, 5),
            cx + s * aw / 2, y - 0.4, 1.0, 0, -0.55 + Math.PI / 2));
        }
      }
    }
  }

  /**
   * Drooping cloth with a ragged bottom edge and missing panels — the single
   * cheapest way to make a street look lived-in and then wrecked.
   */
  _tornSheet(w, h, cols, rng, sag = 0.12) {
    const rows = 3;
    const bottom = [];
    for (let c = 0; c <= cols; c++) {
      bottom.push(-h * (rng() < 0.2 ? 0.45 + rng() * 0.25 : 0.86 + rng() * 0.14));
    }
    const pos = [], uvs = [];
    for (let r = 0; r <= rows; r++) {
      const t = r / rows;
      for (let c = 0; c <= cols; c++) {
        const u = c / cols;
        const y = bottom[c] * t;
        const z = Math.sin(t * Math.PI) * sag + Math.sin(u * Math.PI * 3) * sag * 0.5;
        pos.push(-w / 2 + u * w, y, z);
        uvs.push(u * w * 0.5, t * h * 0.5);
      }
    }
    const idx = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (r === rows - 1 && rng() < 0.16) continue;   // tear a hole
        const a = r * (cols + 1) + c;
        const b = a + 1, cc = a + cols + 1, dd = cc + 1;
        idx.push(a, cc, b, b, cc, dd);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    g.setIndex(idx);
    g.computeVertexNormals();
    return g;
  }

  /** Low annex whose roof is reachable from the market — the map's high ground. */
  _annex() {
    const x = 43, z = 28, w = 11, d = 9.2, h = 4.8;
    this._proxy(w, h, d, x, h / 2, z);
    const core = new THREE.Mesh(boxGeo(w - 1.2, h, d - 1.2, 3), this.mat.interior);
    core.position.set(x, h / 2, z);
    this._add(core, false);
    for (let f = 0; f < 4; f++) {
      this._facade({
        x, z, w, d, floors: 1, mat: 'plasterPale', h, G0: h, FH: 3.2,
        rng: makeRng(9001 + f), face: f, dmg: null, detail: f === 3, shop: true,
      });
    }
    // Walkable roof: deck + parapet in collision so players can hold it.
    const deck = new THREE.Mesh(boxGeo(w - 1.2, 0.3, d - 1.2, 3), this.mat.tile);
    deck.position.set(x, h + 0.15, z);
    this._add(deck);
    // Parapet on three sides; the west side is left open as the way up.
    for (const [ox, oz, pw, pd] of [[0, d / 2 - 0.3, w, 0.5], [0, -d / 2 + 0.3, w, 0.5],
      [w / 2 - 0.3, 0, 0.5, d]]) {
      const p = new THREE.Mesh(boxGeo(pw, 1.05, pd, 2.0), this.mat.plasterPale);
      p.position.set(x + ox, h + 0.82, z + oz);
      this._add(p);
    }
    this._roofPlant(x, z, w - 2, d - 2, h, makeRng(4004), 'plasterPale');

    // Route up: two stacked containers hard against the open side.
    this._container(x - 8.5, z + 3.2, 0.0, 'metalBlue', 0);
    this._container(x - 8.5, z + 3.2, 0.04, 'metalRed', 2.66);
    this.coverPoints.push(new THREE.Vector3(x, h + 0.3, z - d / 2 + 1.2));
  }

  _perimeter() {
    // T-wall blast barrier segments rather than a single grey box.
    const rng = makeRng(777);
    const seg = 3.2;
    const runs = [
      [-108, 104, -108, -104], [-108, 104, 104, 108],
      [-108, -104, -104, 104], [104, 108, -104, 104],
    ];
    for (const [x0, x1, z0, z1] of runs) {
      const alongX = x1 - x0 > z1 - z0;
      const len = alongX ? x1 - x0 : z1 - z0;
      const n = Math.floor(len / seg);
      const cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;
      const solid = new THREE.Mesh(
        boxGeo(alongX ? len : 1.6, 7.2, alongX ? 1.6 : len, 3), this.mat.concrete);
      solid.position.set(cx, 3.6, cz);
      this._add(solid);
      for (let i = 0; i < n; i++) {
        const t = -len / 2 + seg * (i + 0.5);
        const px = alongX ? cx + t : cx;
        const pz = alongX ? cz : cz + t;
        this.B.add('concreteDark', xf(boxGeo(alongX ? 0.28 : 2.0, 7.6, alongX ? 2.0 : 0.28, 2.0), px, 3.8, pz));
        this.B.add('concreteDark', xf(boxGeo(alongX ? seg - 0.1 : 2.2, 0.3, alongX ? 2.2 : seg - 0.1, 2.0), px, 7.5, pz));
        if (rng() > 0.7) {
          this.B.add('graffiti' + ((rng() * 2) | 0), xf(planeGeo(3.0, 1.5),
            alongX ? px : px + (cx > 0 ? -0.85 : 0.85), 2.4 + rng(),
            alongX ? pz + (cz > 0 ? -0.85 : 0.85) : pz, alongX ? 0 : Math.PI / 2));
        }
      }
    }
  }

  /* ------------------------------------------------- street furniture ---- */

  _buildStreetFurniture() {
    const rng = makeRng(2024);

    // Street lights: tapered pole, curved arm, housing. Bloom sources at dusk.
    for (let i = -4; i <= 4; i++) {
      const side = i % 2 === 0 ? -1 : 1;
      const px = side * 11.4;
      const pz = i * 19 + 3;
      this.B.add('gunmetal', xf(new THREE.CylinderGeometry(0.11, 0.2, 0.5, 10), px, 0.5, pz));
      this.B.add('gunmetal', xf(new THREE.CylinderGeometry(0.085, 0.13, 6.6, 10), px, 3.6, pz));
      for (let s = 0; s < 5; s++) {
        const t = s / 4;
        this.B.add('gunmetal', xf(new THREE.CylinderGeometry(0.07, 0.07, 0.6, 6),
          px - side * (0.15 + t * 1.5), 6.85 + Math.sin(t * 1.4) * 0.35, pz, 0, 0, side * (0.5 + t * 0.6)));
      }
      this.B.add('gunmetal', xf(boxGeo(0.85, 0.22, 0.42, 0.6), px - side * 1.85, 7.0, pz, 0, 0, side * 0.12));
      this.B.add('lamp', xf(boxGeo(0.62, 0.06, 0.34, 0.6), px - side * 1.85, 6.87, pz));
      this._proxy(0.34, 6.8, 0.34, px, 3.4, pz);
      if (rng() > 0.55) {
        this.B.add('gunmetal', xf(boxGeo(0.06, 1.6, 0.06, 0.4), px + side * 0.2, 2.6, pz + 0.3));
        this.B.add('metal', xf(boxGeo(0.9, 0.6, 0.06, 0.6), px + side * 0.2, 3.3, pz + 0.3, 0.3));
      }
    }

    // Power poles with catenary cables and hanging lamps across the street.
    const anchors = [];
    for (let i = -3; i <= 3; i++) {
      const pz = i * 24 - 6;
      for (const s of [-1, 1]) {
        const px = s * 12.6;
        this.B.add('wood', xf(new THREE.CylinderGeometry(0.13, 0.17, 8.4, 8), px, 4.2, pz));
        this.B.add('wood', xf(boxGeo(1.7, 0.14, 0.14, 0.8), px, 7.6, pz));
        this.B.add('wood', xf(boxGeo(1.3, 0.12, 0.12, 0.8), px, 7.0, pz));
        this._proxy(0.44, 8.0, 0.44, px, 4.0, pz);
        for (let k = -1; k <= 1; k++) {
          this.B.add('gunmetal', xf(new THREE.CylinderGeometry(0.05, 0.05, 0.22, 6), px + k * 0.7, 7.75, pz));
        }
        anchors.push([px, 7.7, pz, s]);
      }
    }
    // Cross-street spans, sagging, with a few bulbs and pegged laundry.
    for (let i = 0; i < anchors.length; i += 2) {
      const a = anchors[i], b = anchors[i + 1];
      if (!a || !b) continue;
      for (let k = -1; k <= 1; k++) {
        this._cable(
          new THREE.Vector3(a[0], a[1] + k * 0.03, a[2] + k * 0.7),
          new THREE.Vector3(b[0], b[1] + k * 0.03, b[2] + k * 0.7),
          1.6 + Math.abs(k) * 0.4, 0.035, 'gunmetal'
        );
      }
      if (rng() > 0.55) {
        // Washing line: pegged out over the pavement, not the firing lane, and
        // kept small so it silhouettes rather than curtains the street off.
        const zc = a[2] + 4 + rng() * 6;
        this._cable(new THREE.Vector3(a[0], 6.4, zc), new THREE.Vector3(b[0], 6.2, zc), 2.0, 0.024, 'gunmetal');
        for (let c = 0; c < 8; c++) {
          const t = (c + 1) / 9;
          if (t > 0.32 && t < 0.68) continue;
          const lx = THREE.MathUtils.lerp(a[0], b[0], t);
          const ly = THREE.MathUtils.lerp(6.4, 6.2, t) - 2.0 * 4 * t * (1 - t);
          const cw = 0.34 + rng() * 0.24;
          const col = ['fabricCream', 'fabricBlue', 'fabricCream', 'fabricRed'][(rng() * 4) | 0];
          this.B.add(col, xf(this._tornSheet(cw, 0.5 + rng() * 0.35, 4, rng, 0.04), lx, ly - 0.02, zc));
        }
      }
    }
    // Long cables running down the street, low over the road.
    for (const s of [-1, 1]) {
      for (let i = -3; i < 3; i++) {
        this._cable(
          new THREE.Vector3(s * 12.6, 7.55, i * 24 - 6),
          new THREE.Vector3(s * 12.6, 7.55, (i + 1) * 24 - 6),
          1.1, 0.03, 'gunmetal'
        );
      }
    }
  }

  _cable(a, b, sag, radius, key) {
    const pts = [];
    for (let i = 0; i <= 8; i++) {
      const t = i / 8;
      pts.push(new THREE.Vector3(
        THREE.MathUtils.lerp(a.x, b.x, t),
        THREE.MathUtils.lerp(a.y, b.y, t) - sag * 4 * t * (1 - t),
        THREE.MathUtils.lerp(a.z, b.z, t)
      ));
    }
    const curve = new THREE.CatmullRomCurve3(pts);
    this.B.add(key, new THREE.TubeGeometry(curve, 12, radius, 5, false));
  }

  /* -------------------------------------------------- set-piece groups --- */

  _buildCompositions() {
    this._midStreet();
    this._checkpoint(0, 40);
    this._junctionWreck();
    this._market();
    this._collapse();
    this._westAlley();
    this._craters();
    this._barrierLines();
  }

  /**
   * The long north-south sightline needs a mid-field anchor or the street is a
   * shooting gallery. A gutted bus across two lanes gives cover, a flank route
   * behind it, and something for the eye to land on.
   */
  _midStreet() {
    const rng = makeRng(2468);
    this._bus(-3.6, 27, 0.14);
    this._carHulk(7.0, 17, -0.35, true);
    this._billboard(-13.5, 34, 1.35);

    // Debris fan thrown out from under the bus.
    this._rubblePile(-7.2, 33, 3.0, 0.9, rng);
    this._crateStack(4.6, 33, 0.6, 2, rng);
    this._drum(-8.2, 15.5, 'metalYellow');
    this._drum(-8.6, 14.4, 'metalRed', true);
    this._tyreStack(8.2, 25, 3);
    for (let i = 0; i < 3; i++) this._barrier(-7.6 + i * 0.2, 38 + i * 2.3, 0.05);
    this._sandbagWall(7.2, 36, Math.PI / 2, 6, 3);
    this.coverPoints.push(new THREE.Vector3(-3.6, 0, 27), new THREE.Vector3(7.2, 0, 36));
  }

  /** Burnt-out city bus: the map's biggest single piece of storytelling. */
  _bus(x, z, ry) {
    const g = [];
    const L = 10.6, W = 2.55;
    g.push(xf(boxGeo(L, 0.55, W - 0.15, 1.4), 0, 0.62, 0));            // skirt
    g.push(xf(boxGeo(L, 1.0, W, 1.4), 0, 1.35, 0));                    // lower body
    g.push(xf(boxGeo(L, 0.22, W, 1.2), 0, 2.72, 0));                   // cant rail
    g.push(xf(boxGeo(L - 0.3, 0.16, W - 0.2, 1.2), 0, 2.9, 0));        // roof
    // Window pillars between the blown-out glazing.
    for (let i = 0; i < 8; i++) {
      g.push(xf(boxGeo(0.16, 1.3, W + 0.02, 0.5), -L / 2 + 0.5 + i * ((L - 1) / 7), 2.05, 0));
    }
    for (const s of [-1, 1]) {
      g.push(xf(boxGeo(L, 1.3, 0.1, 1.0), 0, 2.05, s * (W / 2 - 0.02)));
    }
    g.push(xf(boxGeo(0.2, 1.3, W, 0.6), L / 2 - 0.1, 2.05, 0));
    g.push(xf(boxGeo(0.2, 1.3, W, 0.6), -L / 2 + 0.1, 2.05, 0));
    // Sheared roof panel peeled up at the rear.
    g.push(xf(boxGeo(2.6, 0.1, W - 0.4, 1.0), -L / 2 + 1.4, 3.25, 0, 0, 0, 0.55));
    this.B.add('burnt', xf(mergeGeometries(g, false), x, 0, z, ry));
    // Gutted interior.
    this.B.add('interior', xf(boxGeo(L - 0.5, 1.25, W - 0.3, 1.0), x, 2.05, z, ry));
    if (!this._tyreList) this._tyreList = [];
    for (const sx of [3.6, -2.6, -3.9]) {
      for (const sz of [1, -1]) {
        this._tyreList.push([
          x + Math.cos(ry) * sx + Math.sin(ry) * sz * 1.2,
          0.5, z - Math.sin(ry) * sx + Math.cos(ry) * sz * 1.2, ry, Math.PI / 2, 1.25,
        ]);
      }
    }
    this.B.add('scorch', xf(new THREE.CircleGeometry(7.5, 22), x, 0.03, z, 0, -Math.PI / 2));
    this._proxy(L + 0.2, 3.1, W + 0.1, x, 1.55, z, ry);
  }

  /** Roadside hoarding: strong vertical in the midfield, and a poster surface. */
  _billboard(x, z, ry) {
    const bw = 5.2, bh = 2.4, base = 2.8;
    const M = new THREE.Matrix4().makeTranslation(x, 0, z).multiply(_m4b.makeRotationY(ry));
    const A = (k, g) => this.B.add(k, g.applyMatrix4(M));
    for (const s of [-1, 1]) {
      A('gunmetal', xf(new THREE.CylinderGeometry(0.11, 0.14, base + bh, 8), s * bw * 0.3, (base + bh) / 2, 0));
    }
    A('gunmetal', xf(boxGeo(bw, 0.14, 0.16, 0.6), 0, base, 0.02));
    A('gunmetal', xf(boxGeo(bw, 0.14, 0.16, 0.6), 0, base + bh, 0.02));
    A('metal', xf(boxGeo(bw, bh, 0.1, 1.6), 0, base + bh / 2, -0.06));
    A('poster1', xf(planeGeo(bw - 0.6, bh - 0.45), 0, base + bh / 2, 0.03));
    for (const s of [-1, 1]) {
      A('gunmetal', xf(boxGeo(0.12, bh, 0.18, 0.5), s * (bw / 2 - 0.06), base + bh / 2, 0.02));
    }
    for (const s of [-1, 1]) {
      A('gunmetal', xf(new THREE.CylinderGeometry(0.05, 0.05, 2.4, 6), s * bw * 0.3, base * 0.55, -0.9, 0, 0.9));
    }
    this._proxy(0.6, base + bh, 0.6, x, (base + bh) / 2, z, ry);
  }

  /** North approach: sandbag emplacement + barrier chicane. */
  _checkpoint(cx, cz) {
    const rng = makeRng(8181);
    this._sandbagWall(-5.4, cz + 2, 0, 9, 3);
    this._sandbagWall(5.0, cz - 4, Math.PI * 0.08, 7, 3);
    this._sandbagWall(4.2, cz - 4.9, Math.PI / 2, 4, 3);
    this.coverPoints.push(new THREE.Vector3(-5.4, 0, cz + 2), new THREE.Vector3(5.0, 0, cz - 4));

    // Guard hut made of stacked concrete blocks — stand-height cover.
    for (let i = 0; i < 4; i++) {
      const w = 1.6, h = 0.45;
      this.B.add('concreteDark', xf(boxGeo(w, h, 0.9, 1.4), -8.2, 0.25 + i * h, cz + 8 + (i % 2) * 0.05));
    }
    this._proxy(1.7, 1.9, 1.0, -8.2, 0.95, cz + 8);

    // Tipped-over kiosk and scattered crates.
    this._crateStack(7.6, cz + 10, 0.3, 3, rng);
    this._crateStack(-7.0, cz - 12, 1.1, 2, rng);
    this._drum(8.4, cz + 6.2, 'metalRed');
    this._drum(9.0, cz + 5.2, 'metalYellow');
    this._drum(8.2, cz + 4.6, 'metalBlue', true);

    this._carHulk(-4.6, cz - 16, 0.42, true);
    this._carHulk(6.2, cz + 18, Math.PI * 0.9, false);
  }

  /** The crossing: the map's centrepiece, so it gets the biggest read. */
  _junctionWreck() {
    const rng = makeRng(6161);
    this._truckHulk(1.5, -12, 0.9);
    this._carHulk(-6.0, -20, -0.6, true);
    this._crater(-3.5, -3, 4.2);
    this._crater(7.5, -22, 3.0);

    // Barrier arc funnelling movement round the wreck.
    for (let i = 0; i < 7; i++) {
      const a = -0.9 + i * 0.3;
      this._barrier(Math.sin(a) * 8.5 - 1, Math.cos(a) * 8.5 - 12, a + Math.PI / 2);
    }
    this._sandbagWall(-8.6, -10, Math.PI / 2, 6, 3);
    this._sandbagWall(8.6, -18, Math.PI / 2, 6, 2);
    this.coverPoints.push(new THREE.Vector3(-8.6, 0, -10), new THREE.Vector3(8.6, 0, -18),
      new THREE.Vector3(1.5, 0, -12));

    // Rubble spill from the corner buildings closing the sightline down.
    this._rubblePile(-13, -24, 5.0, 1.5, rng);
    this._rubblePile(13, -2, 4.0, 1.2, rng);
    this._tyreStack(11.5, -24, 3);
    this._drum(-11.6, -2.6, 'metalGreen');
    this._drum(-12.2, -3.4, 'metalRed', true);
  }

  /**
   * Market lane: the east flanking route, running off the main street through
   * the gap between the two east blocks (z 23..33). Stalls line both sides,
   * which makes it a corridor of waist-high cover with a hard ceiling of cloth.
   */
  _market() {
    const rng = makeRng(1212);

    // Paved lane, one step up from the road.
    const deck = new THREE.Mesh(boxGeo(30, 0.4, 9.6, 2.4), this.mat.tile);
    deck.position.set(31, 0.2, 28);
    this._add(deck);
    for (let i = 0; i < 2; i++) {
      const s = new THREE.Mesh(boxGeo(0.9, 0.2 + i * 0.2, 9.6, 2.0), this.mat.tile);
      s.position.set(16.4 + i * 0.9, (0.2 + i * 0.2) / 2, 28);
      this._add(s);
    }

    const stalls = [
      [21.5, 25.2, 0.06], [27.5, 25.0, -0.08], [33.5, 25.3, 0.04],
      [22.5, 31.0, Math.PI + 0.06], [28.5, 31.2, Math.PI - 0.05],
    ];
    for (const [x, z, ry] of stalls) this._stall(x, z, ry, rng);

    for (let i = 0; i < 6; i++) {
      this._crateStack(20 + rng() * 20, 26.5 + rng() * 3, rng() * 3, 1 + ((rng() * 3) | 0), rng);
    }
    this._tyreStack(38.5, 25.0, 4);
    this._tyreStack(19.5, 31.0, 3);
    this._drum(41.5, 24.6, 'metalBlue');
    this._drum(42.3, 25.2, 'metalRed');
    this._drum(41.8, 25.9, 'metalGreen', true);
    this._carHulk(24.5, 28.2, 0.05, false);

    // Cloth strung between the two blocks turns the lane into an interior.
    for (let i = 0; i < 4; i++) {
      const x = 20 + i * 6;
      this._cable(new THREE.Vector3(x, 5.4, 23.4), new THREE.Vector3(x, 5.6, 32.6), 1.1, 0.026, 'gunmetal');
      for (let c = 0; c < 4; c++) {
        const t = (c + 1) / 5;
        const lz = THREE.MathUtils.lerp(23.4, 32.6, t);
        const ly = THREE.MathUtils.lerp(5.4, 5.6, t) - 1.1 * 4 * t * (1 - t);
        const col = ['fabricRed', 'fabricBlue', 'fabricCream'][(rng() * 3) | 0];
        this.B.add(col, xf(this._tornSheet(0.45 + rng() * 0.35, 0.8, 4, rng, 0.05), x, ly, lz, Math.PI / 2));
      }
    }
    this.coverPoints.push(new THREE.Vector3(21.5, 0, 25.2), new THREE.Vector3(34.5, 0, 30.9),
      new THREE.Vector3(24.5, 0, 28.2));
  }

  _stall(x, z, ry, rng) {
    const w = 3.6, d = 2.2;
    const M = new THREE.Matrix4().makeTranslation(x, 0, z).multiply(_m4b.makeRotationY(ry));
    const A = (k, g) => this.B.add(k, g.applyMatrix4(M));
    // Counter — deliberately 1.0 m: crouch cover.
    A('wood', xf(boxGeo(w, 0.1, d, 1.0), 0, 0.98, 0));
    A('wood', xf(boxGeo(w, 0.55, 0.1, 1.0), 0, 0.68, d / 2 - 0.05));
    for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      A('wood', xf(boxGeo(0.1, 0.95, 0.1, 0.6), sx * (w / 2 - 0.1), 0.47, sz * (d / 2 - 0.1)));
      A('gunmetal', xf(new THREE.CylinderGeometry(0.045, 0.045, 2.5, 6), sx * (w / 2 - 0.05), 1.25, sz * (d / 2 - 0.05)));
    }
    const col = ['fabricRed', 'fabricBlue', 'fabricCream'][(rng() * 3) | 0];
    A(col, xf(this._tornSheet(w + 0.4, d + 0.9, 7, rng, 0.12), 0, 2.45, -0.7, 0, -Math.PI / 2 + 0.1));
    A('gunmetal', xf(new THREE.CylinderGeometry(0.03, 0.03, w + 0.4, 5), 0, 2.48, -0.7, 0, 0, Math.PI / 2));
    // Produce crates on and under the counter.
    for (let i = 0; i < 3; i++) {
      A('wood', xf(boxGeo(0.6, 0.35, 0.45, 0.5), -w / 2 + 0.5 + i * 1.2, 1.2, (rng() - 0.5) * 0.6, rng()));
    }
    this._proxy(w, 1.0, d, x, 0.5, z, ry);
    this.coverPoints.push(new THREE.Vector3(x, 0, z));
  }

  /** West facade blown open — rubble spills across the pavement into the road. */
  _collapse() {
    const rng = makeRng(3434);
    const bx = -16.4, bz = -30;
    this._rubblePile(bx + 3.5, bz, 8.5, 2.4, rng);
    // Leaning slabs make readable stand-height cover on the west lane.
    const slabs = [[-11, -26, 0.4, 0.9], [-9.5, -34, -0.5, 0.7], [-13, -38, 0.2, 1.1]];
    for (const [x, z, ry, tilt] of slabs) {
      const g = boxGeo(4.2, 0.35, 2.6, 2.0);
      this.B.add('concreteDark', xf(g, x, 1.0, z, ry, 0, tilt));
      this._proxy(3.0, 2.0, 2.4, x, 1.0, z, ry);
      this.coverPoints.push(new THREE.Vector3(x, 0, z));
      for (let r = 0; r < 6; r++) {
        this.B.add('gunmetal', xf(new THREE.CylinderGeometry(0.02, 0.02, 0.9 + rng() * 0.8, 5),
          x + (rng() - 0.5) * 3.6, 1.6 + rng() * 0.6, z + (rng() - 0.5) * 2.2,
          rng() * 3, (rng() - 0.5) * 1.2, (rng() - 0.5) * 1.2));
      }
    }
    this._crater(-6, -40, 3.4);
    this._carHulk(-7.4, -46, 2.1, true);
  }

  /** West back lane, between the street blocks (rear at -39) and the second rank. */
  _westAlley() {
    const rng = makeRng(9494);
    const LX = -45;
    for (let i = 0; i < 5; i++) {
      const z = -50 + i * 20 + rng() * 6;
      this._crateStack(LX + (rng() - 0.5) * 5, z, rng() * 3, 1 + ((rng() * 3) | 0), rng);
      this._drum(LX - 3 + rng() * 2, z + 4, ['metalRed', 'metalBlue', 'metalGreen'][(rng() * 3) | 0], rng() > 0.6);
    }
    // Dumpsters — the alley's stand cover.
    for (const [x, z, ry] of [[LX + 2, -18, 0.1], [LX - 2, 12, -0.2], [LX + 1, -44, 0.35]]) {
      this._dumpster(x, z, ry);
      this.coverPoints.push(new THREE.Vector3(x, 0, z));
    }
    this._tyreStack(LX - 3, -30, 4);
    this._carHulk(LX, 26, 0.2, true);
    // Overhead cables give the alley a ceiling.
    for (let i = 0; i < 5; i++) {
      const z = -46 + i * 22;
      this._cable(new THREE.Vector3(-39.5, 5.6, z), new THREE.Vector3(-50.5, 5.2, z), 1.0, 0.024, 'gunmetal');
    }
  }

  _craters() {
    this._crater(-2.5, 22, 3.6);
    this._crater(5.5, -56, 4.6);
    this._crater(-30, 4, 3.0);
    this._crater(38, -8, 3.4);
  }

  _barrierLines() {
    const rng = makeRng(5566);
    const lines = [
      [-6.2, 68, 0.05, 4], [7.5, 56, -0.1, 3], [-7.8, 8, 0.02, 3],
      [7.9, 24, 0.06, 4], [-8.0, -56, 0.0, 4], [7.6, -46, 0.04, 3],
      [-6.5, -70, Math.PI / 2, 3], [3.0, 72, Math.PI / 2, 4],
    ];
    for (const [x, z, ry, n] of lines) {
      for (let i = 0; i < n; i++) {
        const dx = Math.cos(ry) * 0 - Math.sin(ry) * 0;
        const px = x + Math.cos(ry) * 0 + Math.sin(ry) * i * 2.3;
        const pz = z + Math.cos(ry) * i * 2.3;
        this._barrier(px, pz, ry + (rng() - 0.5) * 0.12);
      }
      this.coverPoints.push(new THREE.Vector3(x, 0, z));
    }
  }

  /* ------------------------------------------------------- prop makers --- */

  /** Jersey barrier: chamfered profile, 1.05 m — crouch cover. */
  _barrier(x, z, ry) {
    const g = [];
    g.push(xf(boxGeo(2.3, 0.22, 0.68, 1.2), 0, 0.11, 0));
    g.push(xf(boxGeo(2.3, 0.5, 0.44, 1.2), 0, 0.5, 0));
    g.push(xf(boxGeo(2.3, 0.36, 0.3, 1.2), 0, 0.9, 0));
    // Chamfer strips where the profile steps in.
    g.push(xf(boxGeo(2.3, 0.2, 0.2, 0.8), 0, 0.26, 0.24, 0, 0.6));
    g.push(xf(boxGeo(2.3, 0.2, 0.2, 0.8), 0, 0.26, -0.24, 0, -0.6));
    const merged = mergeGeometries(g, false);
    this.B.add('concreteDark', xf(merged, x, 0, z, ry));
    this._proxy(2.3, 1.1, 0.7, x, 0.55, z, ry);
    this.coverPoints.push(new THREE.Vector3(x, 0, z));
  }

  _sandbagWall(x, z, ry, cols, rows) {
    if (!this._bagList) this._bagList = [];
    const rng = makeRng(((x * 73 + z * 31) | 0) + 991);
    for (let r = 0; r < rows; r++) {
      const n = cols - r;
      for (let c = 0; c < n; c++) {
        const px = (c - (n - 1) / 2) * 0.54 + (r % 2) * 0.14;
        const py = 0.22 + r * 0.36;
        this._bagList.push([
          x + Math.cos(ry) * px, py, z + Math.sin(ry) * px,
          ry + (rng() - 0.5) * 0.25, 0.9 + rng() * 0.2,
        ]);
      }
    }
    const w = cols * 0.54;
    const h = rows * 0.36 + 0.1;
    this._proxy(w, h, 0.62, x, h / 2, z, ry);
  }

  /** `y0` lifts the prop onto a pavement or deck rather than the road. */
  _drum(x, z, colour, tipped = false, y0 = 0) {
    const g = [];
    g.push(new THREE.CylinderGeometry(0.29, 0.29, 0.88, 14));
    for (const y of [-0.24, 0, 0.24]) g.push(xf(new THREE.CylinderGeometry(0.31, 0.31, 0.06, 14), 0, y, 0));
    const merged = mergeGeometries(g, false);
    if (tipped) {
      this.B.add(colour, xf(merged, x, y0 + 0.3, z, 0, Math.PI / 2));
      this._proxy(0.62, 0.6, 0.9, x, y0 + 0.3, z);
    } else {
      this.B.add(colour, xf(merged, x, y0 + 0.46, z, 0));
      this._proxy(0.62, 0.92, 0.62, x, y0 + 0.46, z);
    }
  }

  _crateStack(x, z, ry, n, rng, y0 = 0) {
    for (let i = 0; i < n; i++) {
      const s = 0.62 + rng() * 0.25;
      const g = [];
      g.push(boxGeo(s * 1.25, s * 0.85, s * 1.0, 0.7));
      for (const sx of [-1, 1]) g.push(xf(boxGeo(0.06, s * 0.9, s * 1.05, 0.4), sx * (s * 0.62 - 0.02), 0, 0));
      const merged = mergeGeometries(g, false);
      this.B.add('wood', xf(merged, x + (rng() - 0.5) * 0.3, y0 + 0.45 + i * (s * 0.88), z + (rng() - 0.5) * 0.3,
        ry + (rng() - 0.5) * 0.5));
    }
    this._proxy(1.2, 0.75 * n + 0.2, 1.1, x, y0 + (0.75 * n + 0.2) / 2, z, ry);
    if (n > 1) this.coverPoints.push(new THREE.Vector3(x, 0, z));
  }

  _tyreStack(x, z, n, y0 = 0) {
    if (!this._tyreList) this._tyreList = [];
    const rng = makeRng(((x * 17 + z * 41) | 0) + 55);
    for (let i = 0; i < n; i++) {
      this._tyreList.push([x + (rng() - 0.5) * 0.14, y0 + 0.18 + i * 0.28, z + (rng() - 0.5) * 0.14, rng() * 3]);
    }
    this._proxy(1.1, n * 0.28 + 0.1, 1.1, x, y0 + (n * 0.28) / 2, z);
  }

  _dumpster(x, z, ry) {
    const g = [];
    g.push(xf(boxGeo(2.4, 1.1, 1.3, 1.2), 0, 0.72, 0));
    g.push(xf(boxGeo(2.5, 0.1, 1.4, 1.2), 0, 1.3, -0.3, 0, -0.35));
    for (const sx of [-1, 1]) {
      g.push(xf(boxGeo(0.12, 0.5, 0.12, 0.4), sx * 1.0, 0.22, 0.5));
      g.push(xf(boxGeo(0.12, 0.5, 0.12, 0.4), sx * 1.0, 0.22, -0.5));
    }
    this.B.add('metalGreen', xf(mergeGeometries(g, false), x, 0, z, ry));
    this._proxy(2.5, 1.4, 1.4, x, 0.7, z, ry);
  }

  _container(x, z, ry, colour, y = 0) {
    const g = [];
    g.push(boxGeo(6.06, 2.6, 2.44, 1.6));
    // Corrugation: vertical ribs on both long sides.
    for (let i = 0; i < 13; i++) {
      const px = -2.8 + i * 0.466;
      for (const s of [-1, 1]) g.push(xf(boxGeo(0.2, 2.3, 0.07, 0.4), px, 0, s * 1.24));
    }
    for (const s of [-1, 1]) {
      g.push(xf(boxGeo(6.2, 0.18, 0.18, 0.5), 0, s * 1.3, 1.28));
      g.push(xf(boxGeo(6.2, 0.18, 0.18, 0.5), 0, s * 1.3, -1.28));
    }
    const merged = mergeGeometries(g, false);
    this.B.add(colour, xf(merged, x, y + 1.32, z, ry));
    this._proxy(6.1, 2.62, 2.5, x, y + 1.32, z, ry);
    if (y === 0) this.coverPoints.push(new THREE.Vector3(x, 0, z));
  }

  _carHulk(x, z, ry, burnt) {
    const g = [];
    const key = burnt ? 'burnt' : ['metalBlue', 'metalRed', 'metalGreen', 'metalYellow'][(Math.abs(x * 7 + z) | 0) % 4];
    // Lower body, sills, bonnet, boot.
    g.push(xf(boxGeo(4.3, 0.52, 1.82, 1.2), 0, 0.72, 0));
    g.push(xf(boxGeo(1.5, 0.3, 1.72, 1.0), 1.45, 0.98, 0));
    g.push(xf(boxGeo(1.2, 0.28, 1.72, 1.0), -1.6, 0.96, 0));
    // Cabin with a tapered roof and blown-out glazing.
    g.push(xf(boxGeo(2.1, 0.72, 1.68, 1.0), -0.15, 1.35, 0));
    g.push(xf(boxGeo(1.9, 0.1, 1.55, 1.0), -0.15, 1.7, 0));
    // Wheel arches.
    for (const sx of [1.35, -1.35]) {
      for (const sz of [1, -1]) {
        g.push(xf(new THREE.TorusGeometry(0.46, 0.09, 6, 10, Math.PI), sx, 0.72, sz * 0.86, Math.PI / 2));
      }
    }
    this.B.add(key, xf(mergeGeometries(g, false), x, 0, z, ry));

    // Wheels — burnt cars lose a couple.
    const rng = makeRng(((x * 31 + z * 17) | 0) + 3);
    if (!this._tyreList) this._tyreList = [];
    for (const sx of [1.35, -1.35]) {
      for (const sz of [1, -1]) {
        if (burnt && rng() < 0.35) continue;
        const px = x + Math.cos(ry) * sx + Math.sin(ry) * sz * 0.92;
        const pz = z - Math.sin(ry) * sx + Math.cos(ry) * sz * 0.92;
        this._tyreList.push([px, 0.42, pz, ry, Math.PI / 2]);
      }
    }
    // Dark interior + scorch mark under a burnt shell.
    this.B.add('interior', xf(boxGeo(1.95, 0.6, 1.5, 1.0), x, 0, z, ry).translate(
      -Math.cos(ry) * 0.15, 1.36, Math.sin(ry) * 0.15));
    if (burnt) {
      this.B.add('scorch', xf(new THREE.CircleGeometry(3.4, 18), x, 0.03, z, 0, -Math.PI / 2));
    }
    this._proxy(4.4, 1.5, 1.95, x, 0.75, z, ry);
    this.coverPoints.push(new THREE.Vector3(x, 0, z));
  }

  _truckHulk(x, z, ry) {
    const g = [];
    g.push(xf(boxGeo(7.6, 0.5, 2.5, 1.4), 0, 0.95, 0));
    g.push(xf(boxGeo(2.4, 1.5, 2.5, 1.2), 2.4, 1.9, 0));
    g.push(xf(boxGeo(1.2, 0.6, 2.4, 1.0), 3.9, 1.2, 0));
    // Flatbed sides, one of them torn away.
    g.push(xf(boxGeo(5.0, 0.9, 0.12, 1.0), -1.2, 1.6, 1.19));
    g.push(xf(boxGeo(2.6, 0.9, 0.12, 1.0), -2.4, 1.6, -1.19, 0, 0, 0.25));
    for (let i = 0; i < 4; i++) {
      g.push(xf(new THREE.CylinderGeometry(0.05, 0.05, 2.1, 6), -3.4 + i * 1.6, 2.6, 1.15));
    }
    this.B.add('burnt', xf(mergeGeometries(g, false), x, 0, z, ry));
    if (!this._tyreList) this._tyreList = [];
    for (const sx of [2.6, -1.6, -3.0]) {
      for (const sz of [1, -1]) {
        const px = x + Math.cos(ry) * sx + Math.sin(ry) * sz * 1.25;
        const pz = z - Math.sin(ry) * sx + Math.cos(ry) * sz * 1.25;
        this._tyreList.push([px, 0.55, pz, ry, Math.PI / 2, 1.3]);
      }
    }
    this.B.add('scorch', xf(new THREE.CircleGeometry(5.5, 20), x, 0.03, z, 0, -Math.PI / 2));
    this._proxy(7.8, 2.8, 2.6, x, 1.4, z, ry);
    this.coverPoints.push(new THREE.Vector3(x, 0, z));
  }

  _crater(x, z, r) {
    const rng = makeRng(((x * 13 + z * 29) | 0) + 77);
    const onRoad = Math.abs(x) < 9.6;
    const y = onRoad ? 0.19 - 0.19 * (Math.abs(x) / 9.6) ** 2 : 0.02;
    this.B.add('scorch', xf(new THREE.CircleGeometry(r * 1.35, 22), x, y + 0.007, z, 0, -Math.PI / 2));
    // Ring of upheaved slab and rubble.
    if (!this._rockList) this._rockList = [];
    for (let i = 0; i < 26; i++) {
      const a = rng() * Math.PI * 2;
      const rr = r * (0.55 + rng() * 0.55);
      this._rockList.push([
        x + Math.cos(a) * rr, y + rng() * 0.12, z + Math.sin(a) * rr,
        rng() * 3, 0.18 + rng() * 0.42,
      ]);
    }
    for (let i = 0; i < 7; i++) {
      const a = rng() * Math.PI * 2;
      const rr = r * (0.5 + rng() * 0.4);
      this.B.add('concreteDark', xf(boxGeo(0.7 + rng() * 0.9, 0.16, 0.5 + rng() * 0.7, 1.2),
        x + Math.cos(a) * rr, y + 0.08 + rng() * 0.12, z + Math.sin(a) * rr,
        rng() * 3, (rng() - 0.5) * 0.7, (rng() - 0.5) * 0.7));
    }
    // Broken asphalt lip.
    this.B.add('patch', xf(new THREE.CircleGeometry(r * 0.62, 14), x, y + 0.009, z, 0, -Math.PI / 2));
  }

  _rubblePile(x, z, r, h, rng) {
    if (!this._rockList) this._rockList = [];
    for (let i = 0; i < 90; i++) {
      const a = rng() * Math.PI * 2;
      const t = rng() ** 0.6;
      const rr = r * t;
      this._rockList.push([
        x + Math.cos(a) * rr, 0.05 + (1 - t) * h * rng(), z + Math.sin(a) * rr * 0.8,
        rng() * 3, 0.2 + rng() * 0.65,
      ]);
    }
    for (let i = 0; i < 14; i++) {
      const a = rng() * Math.PI * 2;
      const rr = r * rng() * 0.85;
      this.B.add('concreteDark', xf(boxGeo(0.8 + rng() * 1.6, 0.18, 0.6 + rng() * 1.1, 1.4),
        x + Math.cos(a) * rr, 0.1 + rng() * h * 0.7, z + Math.sin(a) * rr * 0.8,
        rng() * 3, (rng() - 0.5) * 0.9, (rng() - 0.5) * 0.9));
    }
    // A coarse mound for collision so players climb rather than clip.
    const steps = 3;
    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      this._proxy(r * 2 * (1 - t * 0.55), h * 0.9, r * 1.6 * (1 - t * 0.55), x, (h * 0.9) / 2 * (t + 0.25), z);
    }
    this.coverPoints.push(new THREE.Vector3(x, 0, z));
  }

  /* ---------------------------------------------------------- scatter --- */

  _buildScatter() {
    const rng = makeRng(424242);
    const m = new THREE.Matrix4();

    // Sandbags (accumulated by _sandbagWall).
    const bags = this._bagList || [];
    if (bags.length) {
      const bagGeo = new THREE.CapsuleGeometry(0.23, 0.4, 4, 8);
      const im = new THREE.InstancedMesh(bagGeo, this.mat.sandbag, bags.length);
      bags.forEach(([x, y, z, ry, s], i) => {
        _q.setFromEuler(_eu.set(0, ry, Math.PI / 2, 'YXZ'));
        m.compose(_v3.set(x, y, z), _q, _s3.set(s, s, s));
        im.setMatrixAt(i, m);
      });
      im.instanceMatrix.needsUpdate = true;
      im.castShadow = im.receiveShadow = true;
      this.decor.add(im);
    }

    // Tyres (stacks + vehicle wheels).
    const tyres = this._tyreList || [];
    if (tyres.length) {
      const tg = new THREE.TorusGeometry(0.32, 0.13, 8, 14);
      const im = new THREE.InstancedMesh(tg, this.mat.tyre, tyres.length);
      tyres.forEach(([x, y, z, ry, rx = 0, s = 1], i) => {
        _q.setFromEuler(_eu.set(rx === 0 ? Math.PI / 2 : 0, ry, rx === 0 ? 0 : 0, 'YXZ'));
        if (rx !== 0) _q.setFromEuler(_eu.set(0, ry, Math.PI / 2, 'YXZ'));
        m.compose(_v3.set(x, y, z), _q, _s3.set(s, s, s));
        im.setMatrixAt(i, m);
      });
      im.instanceMatrix.needsUpdate = true;
      im.castShadow = im.receiveShadow = true;
      this.decor.add(im);
    }

    // Rubble chunks: craters, piles, plus a light street-wide dusting.
    const rocks = this._rockList || [];
    for (let i = 0; i < 420; i++) {
      let x, z;
      if (i % 3 === 0) { x = (rng() - 0.5) * 19; z = (rng() - 0.5) * 175; }
      else if (i % 3 === 1) { x = -14 - rng() * 6; z = (rng() - 0.5) * 150; }
      else { x = 14 + rng() * 8; z = (rng() - 0.5) * 150; }
      if (Math.abs(x) < 9.6 && rng() < 0.55) continue;   // keep lanes readable
      rocks.push([x, 0.04 + rng() * 0.05, z, rng() * 3, 0.1 + rng() * 0.3]);
    }
    if (rocks.length) {
      const rg = new THREE.IcosahedronGeometry(0.5, 0);
      const pos = rg.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        pos.setXYZ(i, pos.getX(i) * (0.7 + rng() * 0.6), pos.getY(i) * (0.5 + rng() * 0.5), pos.getZ(i) * (0.7 + rng() * 0.6));
      }
      rg.computeVertexNormals();
      const im = new THREE.InstancedMesh(rg, this.mat.concreteDark, rocks.length);
      rocks.forEach(([x, y, z, ry, s], i) => {
        _q.setFromEuler(_eu.set(rng() * 0.8, ry, rng() * 0.8, 'YXZ'));
        m.compose(_v3.set(x, y, z), _q, _s3.set(s, s * 0.7, s));
        im.setMatrixAt(i, m);
      });
      im.instanceMatrix.needsUpdate = true;
      im.castShadow = im.receiveShadow = true;
      this.decor.add(im);
    }

    // Broken bricks — smaller, warmer, hugging the kerbs and rubble spills.
    const bricks = [];
    for (let i = 0; i < 260; i++) {
      const side = rng() > 0.5 ? 1 : -1;
      const near = rng();
      const x = near < 0.6 ? side * (9.8 + rng() * 6) : (rng() - 0.5) * 90;
      const z = near < 0.6 ? (rng() - 0.5) * 175 : (rng() - 0.5) * 175;
      bricks.push([x, 0.05, z, rng() * 3]);
    }
    const bg = boxGeo(0.22, 0.09, 0.11, 0.3);
    const bim = new THREE.InstancedMesh(bg, this.mat.plasterSand, bricks.length);
    bricks.forEach(([x, y, z, ry], i) => {
      _q.setFromEuler(_eu.set(rng() * 0.4, ry, rng() * 0.4, 'YXZ'));
      m.compose(_v3.set(x, y, z), _q, _s3.set(1, 1, 1));
      bim.setMatrixAt(i, m);
    });
    bim.instanceMatrix.needsUpdate = true;
    bim.castShadow = bim.receiveShadow = true;
    this.decor.add(bim);

    // Scattered timber from the market and the collapsed facade.
    const planks = [];
    for (let i = 0; i < 120; i++) {
      const cluster = [[0, -14], [-14, -30], [35, 14], [-40, 0], [0, 40]][(rng() * 5) | 0];
      planks.push([cluster[0] + (rng() - 0.5) * 26, 0.06, cluster[1] + (rng() - 0.5) * 26, rng() * 3]);
    }
    const pg = boxGeo(1.3, 0.05, 0.16, 0.6);
    const pim = new THREE.InstancedMesh(pg, this.mat.wood, planks.length);
    planks.forEach(([x, y, z, ry], i) => {
      _q.setFromEuler(_eu.set(0, ry, (rng() - 0.5) * 0.2, 'YXZ'));
      m.compose(_v3.set(x, y, z), _q, _s3.set(0.7 + rng() * 0.7, 1, 1));
      pim.setMatrixAt(i, m);
    });
    pim.instanceMatrix.needsUpdate = true;
    pim.castShadow = pim.receiveShadow = true;
    this.decor.add(pim);
  }

  /* ------------------------------------------------------------ spawns --- */

  _buildSpawns() {
    this.spawnPoints = [
      new THREE.Vector3(0, 0.4, 62),      // north street
      new THREE.Vector3(-5, 0.4, 42),
      new THREE.Vector3(30, 0.8, 28),     // market lane
      new THREE.Vector3(20, 0.8, 27),
      new THREE.Vector3(6, 0.4, -30),     // south street
      new THREE.Vector3(-4, 0.4, -64),
      new THREE.Vector3(-45, 0.4, -40),   // west back lane
      new THREE.Vector3(-45, 0.4, 20),
      new THREE.Vector3(30, 0.4, -14),    // east cross street
      new THREE.Vector3(-30, 0.4, -14),   // west cross street
    ];
    // Cover points already gathered by the prop makers; add a few sightline
    // anchors the AI can use as fallback positions.
    this.coverPoints.push(
      new THREE.Vector3(-9.0, 0, 46), new THREE.Vector3(9.0, 0, -34),
      new THREE.Vector3(-11.5, 0, 0), new THREE.Vector3(11.5, 0, 14),
      new THREE.Vector3(0, 0, -12)
    );

    // Same bomb contract as the BSP levels, so a game mode needs no special
    // case for the procedural map.
    this.bombSites = Level.bombSites.map((b) => {
      const min = new THREE.Vector3(b.box[0], b.box[1], b.box[2]);
      const max = new THREE.Vector3(b.box[3], b.box[4], b.box[5]);
      return {
        label: b.label,
        box: new THREE.Box3(min, max),
        center: new THREE.Vector3().addVectors(min, max).multiplyScalar(0.5),
      };
    });
    this.teamSpawns = splitSpawns(this.spawnPoints);
  }
}
